import { useState, useEffect } from "react";
import {
  X, Phone, Mail, Building2, User, Calendar, Tag, Clock,
  MessageSquare, Send, PhoneCall, ExternalLink, ChevronRight,
  FileText, Activity, Loader2, CheckCircle2, AlertCircle, RefreshCw,
  CheckCircle, XCircle, CheckSquare, Plus, Check, Circle, Pencil, Trash2, Link,
  CalendarClock,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import RecordAgreementHistory from "@/components/RecordAgreementHistory";
import { resolveS360Name, stripRepCode } from "@/lib/s360Name";
import { toast } from "sonner";
import { LEGACY360_SUBTYPE_CONFIG, resolveLegacy360Subtype } from "@shared/legacy360";
import { Legacy360PreAppointmentChecklist } from "@/components/Legacy360PreAppointmentChecklist";
import { Share360NextStepsChecklist } from "@/components/Share360NextStepsChecklist";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Lead {
  id: number;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  businessName?: string | null;
  repCode?: string | null;
  verticalName?: string | null;
  stage: string;
  notes?: string | null;
  source?: string | null;
  ghlPipelineId?: string | null;
  createdAt: Date | string;
  updatedAt?: Date | string;
  // Fundable360 custom fields
  f360BusinessName?: string | null;
  f360Industry?: string | null;
  f360Revenue?: string | null;
  f360MainGoal?: string | null;
  f360AdditionalInfo?: string | null;
  f360CreditStatus?: string | null;
  f360TimeInBusiness?: string | null;
  f360AffiliateRefId?: string | null;
  f360AgentId?: string | null;
  f360RefId?: string | null;
  f360ReferrerAgentId?: string | null;
  referrerAgentEmail?: string | null;
  referrerAgentName?: string | null;
  referrerAgentContactId?: string | null;
  // Legacy360 custom fields
  l360LeadType?: string | null;
  l360FormName?: string | null;
  l360State?: string | null;
  l360ExistingPlan?: string | null;
  l360TrustGoal?: string | null;
  l360PlanningType?: string | null;
  l360RealEstate?: string | null;
  l360AssetValue?: string | null;
  l360HasBeneficiaries?: string | null;
  l360Timeline?: string | null;
  l360PreferredContact?: string | null;
  l360QuestionsComments?: string | null;
  l360ConsentToContact?: string | null;
  l360PageTitle?: string | null;
  l360PageUrl?: string | null;
  // Legacy360 Advantage fields
  l360CityState?: string | null;
  l360CurrentlyLicensed?: string | null;
  l360LicenseTypes?: string | null;
  l360GoodStanding?: string | null;
  l360IndustryExperience?: string | null;
  l360CurrentRole?: string | null;
  l360ContractedWithAgency?: string | null;
  l360AgencyType?: string | null;
  l360LeadSources?: string | null;
  l360LookingFor?: string | null;
  l360LicenseRevoked?: string | null;
  l360WillingBackgroundCheck?: string | null;
  l360WillingToContract?: string | null;
  // Legacy360 125 Plan fields
  l360Plan125NumEmployees?: string | null;
  l360Plan125NumW2Employees?: string | null;
  l360Plan125PayrollProvider?: string | null;
  l360Plan125HasHealthIns?: string | null;
  l360Plan125PayrollReportUrl?: string | null;
  // Scale360 audit result fields (from old scorecard webhook)
  s360AuditScore?: string | null;
  s360AuditStatus?: string | null;
  s360LossPerYear?: string | null;
  s360LossPerMonth?: string | null;
  s360SavedHours?: string | null;
  s360Roi?: string | null;
  s360PlanName?: string | null;
  s360PlanDesc?: string | null;
  s360PlanPrice?: string | null;
  s360IndustryName?: string | null;
  s360IndustryInsight?: string | null;
  s360IndustryBenefits?: string | null;
  s360PersonaId?: string | null;
  s360PdfLink?: string | null;
  s360EconomyTier?: string | null;
  s360BreakevenMonths?: string | null;
  s360GhostPerYear?: string | null;
  s360AfterPerYear?: string | null;
  s360TimePerYear?: string | null;
  s360Multiplier?: string | null;
  // Scale360 Analysis Report fields (from intake form webhook)
  s360AnalysisProductivityScore?: string | null;
  s360AnalysisBizDesc?: string | null;
  s360AnalysisBusinessSummary?: string | null;
  s360AnalysisPainPoints?: string | null;
  s360AnalysisFutureNarrative?: string | null;
  s360AnalysisMonthlyInefficiency?: string | null;
  s360AnalysisMonthlyTimeCost?: string | null;
  s360AnalysisMissedOpportunity?: string | null;
  s360AnalysisMonthlySavings?: string | null;
  s360AnalysisHoursRecovered?: string | null;
  s360AnalysisExtractedIndustry?: string | null;
  s360AnalysisExtractedTeamSize?: string | null;
  s360AnalysisExtractedRevenue?: string | null;
  s360AnalysisChannels?: string | null;
  s360AnalysisRecommendedPlan?: string | null;
  s360AnalysisPlanName?: string | null;
  s360AnalysisPlanMonthlyPrice?: string | null;
  s360AnalysisPlanAnnualPrice?: string | null;
  s360AnalysisPlanSetupFee?: string | null;
  s360AnalysisPlanReasoning?: string | null;
  s360AnalysisPlanKeyFeatures?: string | null;
  s360AnalysisPlanAssumptions?: string | null;
  s360AnalysisPdfUrl?: string | null;
  s360AnalysisSessionId?: string | null;
  s360AnalysisReportFileName?: string | null;
  s360AnalysisReportGeneratedAt?: string | null;
  s360AnalysisClarificationQuestions?: string | null;
  s360AnalysisMonthlyLoss?: string | null;
  // Share360 form fields
  sh360Role?: string | null;
  sh360BusinessName?: string | null;
  sh360OrgName?: string | null;
  sh360Industry?: string | null;
  sh360MonthlyProcessing?: string | null;
  sh360SupportsNonprofit?: string | null;
  sh360TaxStatus?: string | null;
  sh360AnnualDonations?: string | null;
  sh360SupportersCount?: string | null;
  sh360WorksWith?: string | null;
  sh360NetworkSize?: string | null;
  sh360CityRegion?: string | null;
  sh360State?: string | null;
  sh360JobTitle?: string | null;
  sh360InterestType?: string | null;
  sh360PrimaryGoal?: string | null;
  sh360QuestionsComments?: string | null;
  sh360ConsentToContact?: string | null;
  sh360PageTitle?: string | null;
  sh360PageUrl?: string | null;
  sh360FormName?: string | null;
  ghlStageName?: string | null;
  ghlContactId?: string | null;
  // Media360 form fields stored as JSON metadata
  metadata?: Record<string, unknown> | null;
}

interface LeadDetailDrawerProps {
  lead: Lead | null;
  onClose: () => void;
  isAdmin?: boolean;
  authorName?: string; // rep code or "Admin"
  repCode?: string;
  teamMemberId?: number; // team portal: member ID for reassignment auth
  // Team Portal: true for any logged-in, active team member (team_member or
  // product_manager — not role-restricted). Server-side authorization is
  // enforced independently by clientPortal.generateLinkAsTeamManager
  // (teamMemberProcedure + vertical scoping) — this prop only controls
  // whether the button renders.
  canSendPortal?: boolean;
  // Controls the "Trigger Commission" button specifically. Defaults to
  // `isAdmin` so AdminDashboard (which passes isAdmin={true} and where any
  // admin may always trigger commission) needs no change. TeamMemberDashboard
  // passes this explicitly (role==="product_manager") because it hardcodes
  // isAdmin={true} for every team member regardless of role, but the server
  // (lead.adminTriggerCommission, adminOrTeamManagerProcedure) now only
  // accepts an admin or an active product_manager — this keeps the button
  // from being shown to a caller the server will reject.
  canTriggerCommission?: boolean;
  // Allows the internal Share360 Nonprofit collateral confirmation to be
  // edited. The server independently enforces admin/team authorization.
  canManageShare360InternalSteps?: boolean;
  // Admin-only: stage move (legacy internal keys)
  onStageChange?: (leadId: number, newStage: string) => void;
  availableStages?: { key: string; label: string; color: string }[];
  // Admin-only: GHL real stage move
  onGhlStageChange?: (leadId: number, ghlStageId: string, ghlStageName: string) => void;
  ghlStages?: { id: string; name: string }[];
  calendarUrls?: { label: string; url: string; color: string }[];
  // Admin-only: renders a "Delete" action in the header when provided. Only
  // AdminDashboard passes this (never RepDashboard/TeamMemberDashboard) -
  // presence of the callback is the gate, not the isAdmin flag, since
  // TeamMemberDashboard also hardcodes isAdmin={true} for stage-move access
  // (see canTriggerCommission comment above) and lead.delete is a real
  // adminProcedure that would reject a team-member session anyway. The
  // callback itself does the actual delete (confirmation dialog + mutation
  // already exist in AdminDashboard) - this component only surfaces the entry point.
  onDeleteLead?: (leadId: number) => void;
}

// ─── Stage color helper ───────────────────────────────────────────────────────
const STAGE_COLORS: Record<string, string> = {
  lead_captured: "#6B7280",
  audit_submitted: "#3B82F6",
  follow_up_active: "#F59E0B",
  payment_received: "#8B5CF6",
  onboarding_submitted: "#06B6D4",
  active_client: "#10B981",
  lost_cold: "#EF4444",
};

function stageColor(stage: string) {
  return STAGE_COLORS[stage] ?? "#84ab4a";
}

function stageLabel(stage: string) {
  return stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(date: Date | string) {
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function firstFilled(...values: Array<unknown>): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function getMetaString(metadata: Record<string, unknown> | null | undefined, ...keys: string[]): string | null {
  if (!metadata) return null;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

// ─── Activity icon helper ─────────────────────────────────────────────────────
function ActivityIcon({ type }: { type: string }) {
  const cls = "w-3.5 h-3.5";
  if (type === "stage_change") return <ChevronRight className={cls} />;
  if (type === "note_added") return <MessageSquare className={cls} />;
  if (type === "email_sent") return <Mail className={cls} />;
  if (type === "call_logged") return <PhoneCall className={cls} />;
  if (type === "booking_created") return <Calendar className={cls} />;
  if (type === "created") return <CheckCircle2 className={cls} />;
  return <Activity className={cls} />;
}

// ─── Schedule Consultation link — server-enforced eligibility gate ───────────
// Share360 Business/Nonprofit leads require their client-portal prerequisites
// complete; Legacy360 Living Trust leads require the associate's
// pre-appointment checklist complete (server consultation.checkEligibility
// router). Every other vertical/sub-type returns gated:false and behaves
// exactly as before. While a gate is in shadow mode (its app_settings key
// unset), checkEligibility always returns eligible:true for that vertical, so
// this renders identically to the pre-gate link until the flag is flipped on.
// Mirrors the same component in RepDashboard.tsx's own lead detail panel -
// kept separate rather than shared because the two components' prop
// shapes/styling differ.
function ScheduleConsultationLink({
  lead, fullName, repCode, calendarUrls,
}: {
  lead: Lead;
  fullName: string;
  repCode?: string;
  calendarUrls?: { label: string; url: string; color: string }[];
}) {
  const utils = trpc.useUtils();
  const eligibilityQuery = trpc.consultation.checkEligibility.useQuery(
    { leadId: lead.id },
    { enabled: !!calendarUrls && calendarUrls.length > 0, staleTime: 30_000 },
  );

  if (!calendarUrls || calendarUrls.length === 0) return null;
  const { url, color } = calendarUrls[0];
  const bookingUrl = `${url}?first_name=${encodeURIComponent(lead.firstName ?? "")}&last_name=${encodeURIComponent(lead.lastName ?? "")}&name=${encodeURIComponent(fullName)}&email=${encodeURIComponent(lead.email ?? "")}&phone=${encodeURIComponent(lead.phone ?? "")}&ref=${encodeURIComponent(repCode ?? lead.repCode ?? "")}&ref_code=${encodeURIComponent(repCode ?? lead.repCode ?? "")}&referring_channel=bc360`;

  const data = eligibilityQuery.data;
  if (!data || data.eligible) {
    return (
      <a
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={async (e) => {
          if (!data?.gated) return;
          e.preventDefault();
          const fresh = await utils.consultation.checkEligibility.fetch({ leadId: lead.id });
          if (fresh.eligible) {
            window.open(bookingUrl, "_blank", "noopener,noreferrer");
          } else {
            toast.error(`Not ready to schedule yet — missing: ${fresh.missingDocs.join(", ") || "requirements"}`);
            utils.consultation.checkEligibility.invalidate({ leadId: lead.id });
          }
        }}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors border"
        style={{ background: `${color}15`, color, borderColor: `${color}40` }}
      >
        <Calendar className="w-3.5 h-3.5" /> Schedule Consultation
      </a>
    );
  }

  const missing = data.missingDocs;
  return (
    <button
      disabled
      title={`Missing: ${missing.join(", ")}`}
      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border cursor-not-allowed opacity-60"
      style={{ background: "#F3F4F6", color: "#9CA3AF", borderColor: "#E5E7EB" }}
    >
      <Calendar className="w-3.5 h-3.5" /> Not Ready Yet
    </button>
  );
}

// ─── DocuSeal Submission Row ──────────────────────────────────────────────────
// Shows a single completed DocuSeal submission with a lazy-loaded download link.
// Uses getSubmissionAllR2Docs to cache the PDF in S3 so no DocuSeal login needed.
function DocusealSubmissionRow({ submission }: { submission: any }) {
  const docsQuery = trpc.share360.getSubmissionAllR2Docs.useQuery(
    { submissionId: submission.id },
    { enabled: !!submission.id, staleTime: 300_000 }
  );
  const completedDate = submission.completedAt
    ? new Date(submission.completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  return (
    <div className="flex items-start gap-2 p-2 rounded-lg bg-white border border-blue-100">
      <FileText className="w-3.5 h-3.5 text-[#1A6FD4] mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-gray-800 truncate">
          {submission.templateName ?? `Submission #${submission.id}`}
        </div>
        {completedDate && <div className="text-[10px] text-gray-400">Completed {completedDate}</div>}
        <div className="mt-1">
          {docsQuery.isLoading ? (
            <span className="flex items-center gap-1 text-[10px] text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>
          ) : (docsQuery.data?.docs ?? []).length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {(docsQuery.data!.docs).map((doc, i) => (
                <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] font-semibold text-[#84ab4a] flex items-center gap-0.5 hover:underline">
                  <ExternalLink className="w-3 h-3" /> {doc.name}
                </a>
              ))}
            </div>
          ) : (
            <button onClick={() => docsQuery.refetch()} className="text-[10px] text-red-400 underline">Retry</button>
          )}
        </div>
      </div>
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">Completed</span>
    </div>
  );
}

// ─── Tasks Panel ──────────────────────────────────────────────────────────────
function TasksPanel({ leadId }: { leadId: number }) {
  const utils = trpc.useUtils();
  const tasksQuery = trpc.leadTasks.list.useQuery({ leadId }, { retry: false });
  const createTask = trpc.leadTasks.create.useMutation({
    onSuccess: () => {
      utils.leadTasks.list.invalidate({ leadId });
      setTaskForm({ title: "", description: "", assignedToName: "", dueDate: "" });
      setShowForm(false);
    },
    onError: (e) => toast.error(e.message || "Failed to save task"),
  });
  const updateTask = trpc.leadTasks.update.useMutation({
    onSuccess: () => utils.leadTasks.list.invalidate({ leadId }),
    onError: (e) => toast.error(e.message || "Failed to update task"),
  });
  const deleteTask = trpc.leadTasks.delete.useMutation({
    onSuccess: () => utils.leadTasks.list.invalidate({ leadId }),
    onError: (e) => toast.error(e.message || "Failed to delete task"),
  });
  const [showForm, setShowForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", assignedToName: "", dueDate: "" });

  const tasks = tasksQuery.data ?? [];
  const pending = tasks.filter((t: any) => t.status !== "completed").length;

  const statusIcon = (s: string) =>
    s === "completed" ? <Check className="w-3.5 h-3.5 text-[#84ab4a]" /> :
    s === "in_progress" ? <Clock className="w-3.5 h-3.5 text-yellow-500" /> :
    <Circle className="w-3.5 h-3.5 text-gray-300" />;

  const cycleStatus = (task: any) => {
    const next = task.status === "pending" ? "in_progress" : task.status === "in_progress" ? "completed" : "pending";
    updateTask.mutate({ id: task.id, status: next });
  };

  return (
    <div className="border border-gray-100 rounded-xl p-4 mt-4 bg-gray-50/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-[#84ab4a]" />
          <span className="text-sm font-semibold text-gray-700">Tasks</span>
          {pending > 0 && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-bold">{pending} open</span>}
        </div>
        <button onClick={() => setShowForm(v => !v)} className="text-xs text-[#84ab4a] hover:text-[#6e9038] flex items-center gap-1 font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Task
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3 space-y-2 shadow-sm">
          <input
            className="w-full bg-white border border-gray-200 text-gray-800 text-xs rounded-lg px-3 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 focus:border-[#84ab4a]"
            placeholder="Task title *"
            value={taskForm.title}
            onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
          />
          <input
            className="w-full bg-white border border-gray-200 text-gray-800 text-xs rounded-lg px-3 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 focus:border-[#84ab4a]"
            placeholder="Description (optional)"
            value={taskForm.description}
            onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              className="w-full bg-white border border-gray-200 text-gray-800 text-xs rounded-lg px-3 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 focus:border-[#84ab4a]"
              placeholder="Assign to (name)"
              value={taskForm.assignedToName}
              onChange={e => setTaskForm(f => ({ ...f, assignedToName: e.target.value }))}
            />
            <input
              type="date"
              className="w-full bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 focus:border-[#84ab4a]"
              value={taskForm.dueDate}
              onChange={e => setTaskForm(f => ({ ...f, dueDate: e.target.value }))}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => {
                if (!taskForm.title.trim()) return;
                createTask.mutate({ leadId, title: taskForm.title, description: taskForm.description || undefined, assignedToName: taskForm.assignedToName || undefined, dueDate: taskForm.dueDate || undefined, createdByName: "Admin" });
              }}
              disabled={createTask.isPending || !taskForm.title.trim()}
              className="text-xs bg-[#84ab4a] hover:bg-[#6e9038] text-white font-semibold px-4 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
            >
              {createTask.isPending ? "Saving..." : "Save Task"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs text-gray-400 hover:text-gray-600 px-2 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {tasksQuery.isLoading ? (
        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-gray-300" /></div>
      ) : tasksQuery.isError ? (
        <p className="text-xs text-red-400 text-center py-3">Could not load tasks — {(tasksQuery.error as any)?.message ?? "unknown error"}</p>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">No tasks yet</p>
      ) : (
        <div className="space-y-1.5">
          {tasks.map((task: any) => (
            <div key={task.id} className={`flex items-start gap-2.5 p-2.5 rounded-lg border transition-colors ${task.status === "completed" ? "opacity-50 bg-white border-gray-100" : "bg-white border-gray-100 hover:border-[#84ab4a]/30 hover:shadow-sm"}`}>
              <button onClick={() => cycleStatus(task)} className="mt-0.5 flex-shrink-0" title="Cycle status">
                {statusIcon(task.status)}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-medium leading-snug ${task.status === "completed" ? "line-through text-gray-400" : "text-gray-800"}`}>{task.title}</p>
                {task.description && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{task.description}</p>}
                <div className="flex items-center gap-2 mt-1">
                  {task.assignedToName && <span className="text-[10px] text-gray-400 font-medium">&rarr; {task.assignedToName}</span>}
                  {task.dueDate && <span className="text-[10px] text-gray-400">Due {task.dueDate}</span>}
                </div>
              </div>
              <button
                onClick={() => deleteTask.mutate({ id: task.id })}
                className="text-gray-300 hover:text-red-400 text-[10px] transition-colors flex-shrink-0 mt-0.5"
                title="Delete task"
              >&#x2715;</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LeadDetailDrawer({
  lead: initialLead,
  onClose,
  isAdmin = false,
  authorName = "Admin",
  repCode,
  teamMemberId,
  onStageChange,
  availableStages,
  onGhlStageChange,
  ghlStages,
  calendarUrls,
  onDeleteLead,
  canSendPortal = false,
  canTriggerCommission,
  canManageShare360InternalSteps = false,
}: LeadDetailDrawerProps) {
  const resolvedCanTriggerCommission = canTriggerCommission ?? isAdmin;
  const [activeTab, setActiveTab] = useState<"overview" | "activity" | "notes">("overview");
  const [noteText, setNoteText] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [localLead, setLocalLead] = useState(initialLead);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignConfirm, setReassignConfirm] = useState(false);
  const [selectedRepCode, setSelectedRepCode] = useState("");
  const [showFeeInputs, setShowFeeInputs] = useState(false);
  const [triggerSetupFee, setTriggerSetupFee] = useState("");
  const [triggerMonthlyFee, setTriggerMonthlyFee] = useState("");
  const utils = trpc.useUtils();

  // Keep localLead in sync when a DIFFERENT lead is selected (id changes).
  // Intentionally NOT watching the full initialLead reference — React Query emits
  // a new array/object reference on every background refetch, which would reset
  // localLead and discard any GHL-enriched data written by syncFromGhl.
  useEffect(() => {
    setLocalLead(initialLead);
    setReassignOpen(false);
    setReassignConfirm(false);
    setSelectedRepCode("");
  }, [initialLead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merged lead: localLead (GHL-enriched) takes precedence over prop
  const lead = localLead ?? initialLead;

  const triggerCommissionMutation = trpc.lead.adminTriggerCommission.useMutation({
    onSuccess: (result: any) => {
      utils.lead.list.invalidate();
      if (result?.commissionCreated) {
        toast.success(result.message ?? "Commission created.");
        setShowFeeInputs(false);
        setTriggerSetupFee("");
        setTriggerMonthlyFee("");
      } else {
        // Trigger ran without throwing, but no commission row was actually created —
        // show this as a warning, not a success, so the admin doesn't think it worked.
        toast.warning(result?.message ?? "Trigger ran, but no commission was created.");
      }
    },
    onError: (e: any) => toast.error(e.message ?? "Commission trigger failed"),
  });

  // Team Portal manager: send the same Share360 client document-upload portal
  // an associate can send, via the manager-authorized mutation (server verifies
  // the caller is an active product_manager for this lead's vertical family).
  const [managerPortalLink, setManagerPortalLink] = useState<string | null>(null);
  const sendPortalAsManagerMutation = trpc.clientPortal.generateLinkAsTeamManager.useMutation({
    onSuccess: (data: any, vars) => {
      setManagerPortalLink(data.portalUrl);
      navigator.clipboard.writeText(data.portalUrl);
      if (data.ghlUpdateStatus === "failed") {
        toast.warning("Portal link generated. Wibiz contact update failed — share the link manually as the client may not receive an automated email.");
      } else if (vars.notifyClient) {
        toast.success("Portal link sent and copied to clipboard!");
      } else {
        toast.success("Portal link copied to clipboard!");
      }
    },
    onError: (e: any) => toast.error(e.message ?? "Could not send portal link."),
  });

  // Sync from GHL: auto-trigger when vertical-specific fields are all empty and ghlContactId is set
  const syncMutation = trpc.lead.syncFromGhl.useMutation({
    onSuccess: (data: any) => {
      if (data.synced && data.lead) {
        setLocalLead(data.lead as any);
        // Bust the list cache so the updated sh360 fields (org name, role, etc.) are
        // present on the NEXT drawer open — preventing the hasFormFields=false → auto-sync
        // → snap cycle from repeating for the same lead.
        utils.lead.list.invalidate();
        if (data.fieldsUpdated > 0) toast.success(`Synced ${data.fieldsUpdated} field${data.fieldsUpdated === 1 ? "" : "s"} from Wibiz`);
      }
    },
    onError: () => toast.error("Sync failed — please try again"),
  });

  // Auto-sync once per lead open when GHL contact exists and form fields look empty.
  // Also auto-syncs Scale360 leads that have no analysis data yet — the analysis
  // webhook may not have fired, so sync pulls the data from GHL automatically.
  useEffect(() => {
    if (!initialLead?.id || !initialLead.ghlContactId) return;
    const hasFormFields = !!(
      initialLead.f360BusinessName || initialLead.f360Industry || initialLead.f360Revenue || initialLead.f360CreditStatus ||
      initialLead.f360TimeInBusiness || initialLead.f360MainGoal || initialLead.f360AdditionalInfo ||
      initialLead.l360LeadType || initialLead.l360State || initialLead.l360TrustGoal || initialLead.l360PlanningType ||
      initialLead.l360CityState || initialLead.l360CurrentlyLicensed || initialLead.l360CurrentRole ||
      initialLead.s360AuditScore || initialLead.s360PlanName || initialLead.s360EconomyTier ||
      initialLead.s360IndustryInsight || (initialLead as any).s360GhostPerYear || (initialLead as any).s360IndustryBenefits ||
      initialLead.sh360Role || initialLead.sh360BusinessName || initialLead.sh360OrgName ||
      initialLead.sh360State || initialLead.sh360JobTitle || initialLead.sh360InterestType
    );
    // For Scale360 leads: also auto-sync if the analysis report fields are missing,
    // even when audit fields exist. Analysis data comes from a separate GHL webhook
    // that may not have fired, so pulling from GHL on drawer open fills the gap.
    const isScale360Lead = (initialLead.verticalName ?? "").startsWith("Scale360");
    const hasAnalysisData = !!(
      (initialLead as any).s360AnalysisSessionId ||
      (initialLead as any).s360AnalysisBizDesc ||
      (initialLead as any).s360AnalysisProductivityScore
    );
    if (!hasFormFields || (isScale360Lead && !hasAnalysisData)) {
      syncMutation.mutate({ leadId: initialLead.id });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLead?.id]);

  const isAppointmentBookedStage = ((lead as any)?.ghlStageName || (lead?.stage ? stageLabel(lead.stage) : "")) === "Appointment Booked";
  const appointmentQuery = trpc.lead.getAppointment.useQuery(
    { leadId: lead?.id ?? 0 },
    { enabled: !!lead?.id && isAppointmentBookedStage }
  );

  const notesQuery = trpc.leadNotes.getNotes.useQuery(
    { leadId: initialLead?.id ?? 0 },
    { enabled: !!initialLead?.id }
  );
  const activityQuery = trpc.leadNotes.getActivity.useQuery(
    { leadId: initialLead?.id ?? 0 },
    { enabled: !!initialLead?.id }
  );
  const clientDocsQuery = trpc.clientPortal.getDocuments.useQuery(
    { leadId: initialLead?.id ?? 0 },
    { enabled: !!initialLead?.id }
  );
  const docusealSubmissionsQuery = trpc.share360.getLeadSubmissions.useQuery(
    { email: (initialLead as any)?.email ?? "" },
    { enabled: !!((initialLead as any)?.email), staleTime: 60_000 }
  );
  const reviewDocMutation = trpc.clientPortal.reviewDocument.useMutation({
    onSuccess: () => { clientDocsQuery.refetch(); toast.success("Document updated"); setRejectDocId(null); setRejectNote(""); },
    onError: (e) => toast.error(e.message),
  });
  const [rejectDocId, setRejectDocId] = useState<number | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const repsQuery = trpc.rep.list.useQuery(undefined, { enabled: isAdmin, staleTime: 60_000 });
  const reassignMutation = trpc.lead.reassign.useMutation({
    onSuccess: (data) => {
      setLocalLead((prev) => prev ? {
        ...prev,
        repCode: data.newRepCode,
        f360AgentId: data.newRepCode,
        f360ReferrerAgentId: data.newRepCode,
        referrerAgentName: data.referrerAgentName,
        referrerAgentEmail: data.referrerAgentEmail,
      } : prev);
      utils.lead.list.invalidate();
      activityQuery.refetch();
      setReassignOpen(false);
      setReassignConfirm(false);
      setSelectedRepCode("");
      // A client already in a paid stage is re-checked for their commission
      // when an associate is assigned, so say what happened rather than
      // leaving the admin to wonder whether they still need to trigger it.
      const outcome = (data as any).commissionOutcome as string | undefined;
      if (outcome === "created") {
        toast.success(`Lead reassigned to ${data.newRepCode}. Enrollment commission created.`);
      } else if (outcome === "unknown_program") {
        toast.warning(`Lead reassigned to ${data.newRepCode}. No commission yet: this client's Fundable360 program isn't recorded. Move them to the matching program stage in Wibiz and it will be created automatically.`);
      } else if (outcome === "error") {
        toast.warning(`Lead reassigned to ${data.newRepCode}, but the commission check failed. Use Trigger Commission to retry.`);
      } else {
        toast.success(`Lead reassigned to ${data.newRepCode}`);
      }
    },
    onError: (e) => toast.error(e.message ?? "Reassignment failed"),
  });

  const addNoteMutation = trpc.leadNotes.addNote.useMutation({
    onSuccess: () => {
      setNoteText("");
      notesQuery.refetch();
      activityQuery.refetch();
      toast.success("Note added");
    },
    onError: () => toast.error("Failed to add note"),
  });
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [deleteNoteId, setDeleteNoteId] = useState<number | null>(null);
  const updateNoteMutation = trpc.leadNotes.updateNote.useMutation({
    onSuccess: () => { notesQuery.refetch(); setEditingNoteId(null); setEditingNoteText(""); toast.success("Note updated"); },
    onError: () => toast.error("Failed to update note"),
  });
  const deleteNoteMutation = trpc.leadNotes.deleteNote.useMutation({
    onSuccess: () => { notesQuery.refetch(); setDeleteNoteId(null); toast.success("Note deleted"); },
    onError: () => toast.error("Failed to delete note"),
  });

  // Contact detail inline edit
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const editContactMutation = trpc.lead.edit.useMutation({
    onSuccess: (updated: any) => {
      setLocalLead((prev) => prev ? { ...prev, ...contactDraft } : prev);
      utils.lead.list.invalidate();
      setEditingContact(false);
      toast.success("Contact details saved");
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to save contact details"),
  });

  const openContactEdit = () => {
    if (!lead) return;
    setContactDraft({
      firstName: lead.firstName ?? "",
      lastName: lead.lastName ?? "",
      email: lead.email ?? "",
      phone: lead.phone ?? "",
    });
    setEditingContact(true);
  };

  const saveContactEdit = () => {
    if (!lead) return;
    const trimmed = {
      firstName: contactDraft.firstName.trim(),
      lastName: contactDraft.lastName.trim(),
      email: contactDraft.email.trim(),
      phone: contactDraft.phone.trim(),
    };
    if (!trimmed.firstName) { toast.error("First name is required"); return; }
    editContactMutation.mutate({ id: lead.id, ...trimmed });
  };

  if (!lead) return null;

  // Derive vertical flags from verticalName when known.
  // Only fall back to field-presence when verticalName is absent — this prevents
  // cross-vertical card leakage when a GHL contact has fields from multiple verticals
  // (e.g., a contact who submitted both a Fundable360 and a Share360 form).
  const vName = (lead.verticalName ?? "").trim();
  const showF360Card = vName
    ? vName === "Fundable360"
    : !!(lead.f360BusinessName || lead.f360Industry || lead.f360Revenue || lead.f360CreditStatus ||
         lead.f360TimeInBusiness || lead.f360MainGoal || lead.f360AdditionalInfo || lead.f360AffiliateRefId);
  const showSh360Card = vName
    ? vName.startsWith("Share360")
    : !!(lead.sh360Role || lead.sh360BusinessName || lead.sh360OrgName || lead.sh360Industry ||
         lead.sh360TaxStatus || lead.sh360WorksWith || lead.sh360CityRegion || lead.sh360State ||
         lead.sh360JobTitle || lead.sh360InterestType || lead.sh360PrimaryGoal ||
         lead.sh360QuestionsComments || lead.sh360FormName);
  const showS360Card = vName
    ? vName === "Scale360"
    : !!(lead.s360AuditScore || lead.s360AuditStatus || lead.s360LossPerYear || lead.s360PlanName ||
         lead.s360IndustryName || lead.s360EconomyTier || lead.s360PdfLink);
  const showS360AnalysisCard = !!(
    lead.s360AnalysisBizDesc ||
    lead.s360AnalysisMonthlyInefficiency ||
    lead.s360AnalysisMonthlySavings ||
    lead.s360AnalysisHoursRecovered ||
    lead.s360AnalysisRecommendedPlan ||
    lead.s360AnalysisProductivityScore ||
    lead.s360AnalysisMonthlyLoss ||
    lead.s360AnalysisFutureNarrative ||
    lead.s360AnalysisPainPoints ||
    lead.s360AnalysisBusinessSummary ||
    lead.s360AnalysisExtractedIndustry ||
    lead.s360AnalysisPdfUrl ||
    lead.s360AnalysisPlanKeyFeatures ||
    lead.s360AnalysisReportFileName
  );
  const leadMeta = (lead.metadata as Record<string, unknown> | null | undefined) ?? null;
  const share360SubmissionType = firstFilled(
    lead.sh360Role,
    getMetaString(leadMeta, "submission_type"),
  )?.toLowerCase();
  const isShare360Business = lead.verticalName === "Share360 Business" || share360SubmissionType === "business";
  const isShare360Nonprofit = lead.verticalName === "Share360 Nonprofit" || share360SubmissionType === "nonprofit";
  const f360BusinessName = firstFilled(
    lead.f360BusinessName,
    lead.businessName,
    getMetaString(leadMeta, "business_name_f360", "business_name", "Business Name"),
  );
  const f360Industry = firstFilled(
    lead.f360Industry,
    getMetaString(leadMeta, "industry__business_type", "industry", "Industry", "Industry / Type"),
  );
  const f360TimeInBusiness = firstFilled(
    lead.f360TimeInBusiness,
    getMetaString(leadMeta, "timeinbusiness", "time_in_business", "How long have you been in business?"),
  );
  const f360Revenue = firstFilled(
    lead.f360Revenue,
    getMetaString(leadMeta, "revenue", "monthly_revenue", "Current estimated Monthly Revenue?", "Monthly Revenue"),
  );
  const f360CreditStatus = firstFilled(
    lead.f360CreditStatus,
    getMetaString(leadMeta, "credit_stat", "credit_status", "What describes your current business credit situation?", "Credit Status"),
  );
  const f360MainGoal = firstFilled(
    lead.f360MainGoal,
    getMetaString(leadMeta, "contactf360_main_goal", "main_goal", "What is your main goal with Fundable360?", "Main Goal"),
  );
  const f360AdditionalInfo = firstFilled(
    lead.f360AdditionalInfo,
    getMetaString(leadMeta, "contactf360_additional_info", "additional_info", "Additional Info"),
  );
  const f360SmsEmailConsent = firstFilled(
    getMetaString(leadMeta, "smsConsent", "sms_consent"),
  );
  const f360MarketingConsent = firstFilled(
    getMetaString(leadMeta, "marketingConsent", "marketing_consent"),
  );
  const associateRepCode = firstFilled(
    lead.repCode,
    lead.f360AgentId,
    lead.f360ReferrerAgentId,
    lead.f360AffiliateRefId,
    lead.f360RefId,
  );

  const fullName = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
  const initials = [lead.firstName?.[0], lead.lastName?.[0]].filter(Boolean).join("").toUpperCase() || "?";

  const authorRole: "rep" | "admin" | "team" =
    repCode ? "rep" : teamMemberId ? "team" : "admin";

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSubmittingNote(true);
    await addNoteMutation.mutateAsync({
      leadId: lead.id,
      repCode: repCode,
      authorName,
      authorRole,
      content: noteText.trim(),
    });
    setSubmittingNote(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer panel */}
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-4 p-5 border-b border-gray-100 bg-gradient-to-r from-[#0A1628] to-[#0D1E35]">
          <div className="w-12 h-12 rounded-2xl bg-[#84ab4a]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[#84ab4a] font-bold text-lg">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            {/* Title: always contact name — stable, never snaps on auto-sync */}
            <div className="text-white font-bold text-lg leading-tight truncate">
              {stripRepCode(
                [lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email?.split("@")[0] || "Unknown",
                lead.repCode
              ) ?? "Unknown"}
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${stageColor(lead.stage)}25`, color: stageColor(lead.stage) }}
              >
                {(lead as any).ghlStageName || stageLabel(lead.stage)}
              </span>
              {lead.verticalName && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/60">
                  <Tag className="w-2.5 h-2.5" /> {lead.verticalName}
                </span>
              )}
              {lead.repCode && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-white/50">
                  <User className="w-2.5 h-2.5" /> {lead.repCode}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
            {lead.ghlContactId && (
              <button
                onClick={() => syncMutation.mutate({ leadId: lead.id })}
                disabled={syncMutation.isPending}
                title="Sync latest form data from Wibiz"
                className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/80 transition-colors mt-1"
              >
                <RefreshCw className={`w-3 h-3 ${syncMutation.isPending ? "animate-spin" : ""}`} />
                <span>{syncMutation.isPending ? "Syncing…" : "Sync"}</span>
              </button>
            )}
            {onDeleteLead && (
              <button
                onClick={() => onDeleteLead(lead.id)}
                title="Delete this lead"
                className="flex items-center gap-1 text-[10px] text-red-400/70 hover:text-red-300 transition-colors mt-1"
              >
                <Trash2 className="w-3 h-3" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>

        {/* Quick action bar */}
        <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-100">
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
            >
              <PhoneCall className="w-3.5 h-3.5" /> Call
            </a>
          )}
          {lead.email && (
            <a
              href={`mailto:${lead.email}`}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
          )}
          <ScheduleConsultationLink lead={lead} fullName={fullName} repCode={repCode} calendarUrls={calendarUrls} />
          {isAdmin && onGhlStageChange && ghlStages && ghlStages.length > 0 && (
            <select
              className="ml-auto text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 cursor-pointer"
              value={(lead as any).ghlStageId ?? ""}
              onChange={(e) => {
                const stage = ghlStages.find(s => s.id === e.target.value);
                if (stage) onGhlStageChange(lead.id, stage.id, stage.name);
              }}
            >
              <option value="" disabled>Move to stage...</option>
              {ghlStages.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
          {isAdmin && onStageChange && availableStages && !onGhlStageChange && (
            <select
              className="ml-auto text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 cursor-pointer"
              value={lead.stage}
              onChange={(e) => onStageChange(lead.id, e.target.value)}
            >
              {availableStages.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          )}
        </div>

        {/*
          The checklist and tab content must share one scroller. Keeping the
          checklist outside it can consume nearly the full drawer height on a
          short screen and leave the actual lead details trapped in a tiny
          independent scroll area.
        */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="lead-details-scroll-region">
          {/* Legacy360 Living Trust - pre-appointment checklist (server-enforced consultation gate) */}
          {lead.verticalName === "Legacy360" && resolveLegacy360Subtype(lead as any) === "living_trust" && (
            <div className="px-5 py-3 border-b border-gray-100">
              <Legacy360PreAppointmentChecklist
                leadId={lead.id}
                checklist={(leadMeta?.l360PreAppointmentChecklist as any) ?? null}
              />
            </div>
          )}

          {/* Share360 Business/Nonprofit - Associate's Next Steps (server-enforced consultation gate) */}
          {(isShare360Business || isShare360Nonprofit) && (
            <div className="px-5 py-3 border-b border-gray-100">
              <Share360NextStepsChecklist
                leadId={lead.id}
                isNonprofit={isShare360Nonprofit}
                canManageInternalSteps={canManageShare360InternalSteps}
              />
            </div>
          )}

          {/* Tabs stay reachable after the checklist scrolls out of view. */}
          <div className="sticky top-0 z-10 flex border-b border-gray-100 bg-white">
            {(["overview", "activity", "notes"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-xs font-semibold capitalize transition-colors ${
                  activeTab === tab
                    ? "text-[#84ab4a] border-b-2 border-[#84ab4a]"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab === "notes" ? `Notes (${notesQuery.data?.length ?? 0})` : tab}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div>
          {/* ── Overview ── */}
          {activeTab === "overview" && (
            <div className="p-5 space-y-4">
              {/* Contact details */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Contact Details</div>
                  {!editingContact && (
                    <button
                      onClick={openContactEdit}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#84ab4a] transition-colors"
                      title="Edit contact details"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  )}
                </div>

                {editingContact ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">First Name</label>
                        <input
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#84ab4a]"
                          value={contactDraft.firstName}
                          onChange={e => setContactDraft(d => ({ ...d, firstName: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">Last Name</label>
                        <input
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#84ab4a]"
                          value={contactDraft.lastName}
                          onChange={e => setContactDraft(d => ({ ...d, lastName: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">Email</label>
                      <input
                        type="email"
                        className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#84ab4a]"
                        value={contactDraft.email}
                        onChange={e => setContactDraft(d => ({ ...d, email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">Phone</label>
                      <input
                        type="tel"
                        className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#84ab4a]"
                        value={contactDraft.phone}
                        onChange={e => setContactDraft(d => ({ ...d, phone: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={saveContactEdit}
                        disabled={editContactMutation.isPending}
                        className="flex-1 text-xs font-semibold py-1.5 rounded-lg bg-[#84ab4a] text-white hover:bg-[#6e9038] disabled:opacity-50 transition-colors"
                      >
                        {editContactMutation.isPending ? "Saving…" : "Save"}
                      </button>
                      <button
                        onClick={() => setEditingContact(false)}
                        disabled={editContactMutation.isPending}
                        className="flex-1 text-xs font-semibold py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {lead.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${lead.email}`} className="text-sm text-blue-600 hover:underline truncate">{lead.email}</a>
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${lead.phone}`} className="text-sm text-gray-700">{lead.phone}</a>
                      </div>
                    )}
                    {(() => {
                      const meta = lead.metadata as Record<string, unknown> | null | undefined;
                      const metaOrg = meta ? String(meta["SH360 Org Name"] || meta["SH360 Business Name"] || meta["Organization Name"] || meta["Business Name"] || meta["Org Name"] || "").trim() : "";
                      const orgDisplay = showF360Card || showSh360Card
                        ? null
                        : lead.businessName ?? (metaOrg || null);
                      return orgDisplay ? (
                        <div className="flex items-center gap-3">
                          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{orgDisplay}</span>
                        </div>
                      ) : null;
                    })()}
                    {lead.source && !lead.source.startsWith("ghl_") && (
                      <div className="flex items-center gap-3">
                        <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-500 capitalize">
                          {lead.source === "rep_submission" ? "Rep Submission" :
                           lead.source === "crm_import" ? "CRM Import" :
                           lead.source === "bc360-referral" ? "Referral" :
                           lead.source}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-500">
                        Booked {new Date(lead.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Stage info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Pipeline Stage</div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: stageColor(lead.stage) }} />
                  <span className="text-sm font-semibold text-gray-700">{(lead as any).ghlStageName || stageLabel(lead.stage)}</span>
                </div>
                {isAppointmentBookedStage && appointmentQuery.data && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                    <CalendarClock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span>
                      {new Date(appointmentQuery.data.startTime).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                    </span>
                  </div>
                )}
                {lead.verticalName && (
                  <div className="mt-2 text-xs text-gray-400">Vertical: <span className="font-semibold text-gray-600">{lead.verticalName}</span></div>
                )}
                {resolvedCanTriggerCommission && lead.id && lead.stage !== "payment_received" && (
                  <div className="mt-3">
                    {!showFeeInputs ? (
                      <button
                        onClick={() => {
                          setTriggerSetupFee((lead as any).setupFee != null ? String((lead as any).setupFee) : "");
                          setTriggerMonthlyFee((lead as any).monthlyFee != null ? String((lead as any).monthlyFee) : "");
                          setShowFeeInputs(true);
                        }}
                        className="w-full text-xs font-semibold px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                      >
                        Trigger Commission
                      </button>
                    ) : (
                      <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 space-y-2">
                        <div className="text-[11px] text-gray-500">
                          Confirm the fees actually paid — commission is calculated from these. Leave blank only if fees are already recorded on this lead.
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Setup Fee ($)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={triggerSetupFee}
                              onChange={(e) => setTriggerSetupFee(e.target.value)}
                              placeholder={(lead as any).setupFee != null ? String((lead as any).setupFee) : "0.00"}
                              className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-purple-400"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Monthly Fee ($)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={triggerMonthlyFee}
                              onChange={(e) => setTriggerMonthlyFee(e.target.value)}
                              placeholder={(lead as any).monthlyFee != null ? String((lead as any).monthlyFee) : "0.00"}
                              className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-purple-400"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              triggerCommissionMutation.mutate({
                                leadId: lead.id!,
                                ...(triggerSetupFee ? { setupFee: parseFloat(triggerSetupFee) } : {}),
                                ...(triggerMonthlyFee ? { monthlyFee: parseFloat(triggerMonthlyFee) } : {}),
                              })
                            }
                            disabled={triggerCommissionMutation.isPending}
                            className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
                          >
                            {triggerCommissionMutation.isPending ? "Triggering…" : "Confirm & Trigger"}
                          </button>
                          <button
                            onClick={() => setShowFeeInputs(false)}
                            disabled={triggerCommissionMutation.isPending}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Team Portal: Send Portal — same client document-upload
                  portal an associate can send. Rendered for any active team
                  member (canSendPortal); server independently re-verifies
                  authorization (and vertical scope) on every call. */}
              {canSendPortal && lead.verticalName?.startsWith("Share360") && lead.id && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Client Portal</div>
                  {managerPortalLink ? (
                    <div className="space-y-2">
                      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-[#84ab4a] truncate">{managerPortalLink}</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { navigator.clipboard.writeText(managerPortalLink); toast.success("Link copied!"); }}
                          className="flex-1 text-xs font-semibold text-[#84ab4a] border border-[#84ab4a]/30 bg-[#84ab4a]/5 rounded-lg px-2.5 py-1.5 hover:bg-[#84ab4a]/10 transition-colors"
                        >
                          Copy Link
                        </button>
                        <button
                          onClick={() => sendPortalAsManagerMutation.mutate({ leadId: lead.id!, notifyClient: true })}
                          disabled={sendPortalAsManagerMutation.isPending || !lead.email}
                          title="Generate a new link and re-notify the client by email"
                          className="flex-1 text-xs font-semibold text-gray-500 border border-gray-200 bg-white rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {sendPortalAsManagerMutation.isPending && sendPortalAsManagerMutation.variables?.notifyClient ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                          {sendPortalAsManagerMutation.isPending && sendPortalAsManagerMutation.variables?.notifyClient ? "Sending…" : "Re-send"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => sendPortalAsManagerMutation.mutate({ leadId: lead.id!, notifyClient: true })}
                        disabled={sendPortalAsManagerMutation.isPending || !lead.email}
                        title={lead.email ? "Generate a portal link and notify the client by email" : "This lead has no email address on file"}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#84ab4a] text-white hover:bg-[#6d9040] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                      >
                        {sendPortalAsManagerMutation.isPending && sendPortalAsManagerMutation.variables?.notifyClient ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        {sendPortalAsManagerMutation.isPending && sendPortalAsManagerMutation.variables?.notifyClient ? "Sending…" : "Send Portal"}
                      </button>
                      <button
                        onClick={() => sendPortalAsManagerMutation.mutate({ leadId: lead.id!, notifyClient: false })}
                        disabled={sendPortalAsManagerMutation.isPending || !lead.email}
                        title={lead.email ? "Generate a portal link without emailing the client" : "This lead has no email address on file"}
                        className="text-xs font-semibold text-gray-500 border border-gray-200 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                      >
                        {sendPortalAsManagerMutation.isPending && sendPortalAsManagerMutation.variables?.notifyClient === false ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link className="w-3 h-3" />}
                        Get Link
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Admin: Assigned Associate — reassignment panel */}
              {isAdmin && (
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" /> Assigned Associate
                    </div>
                    {!reassignOpen && (
                      <button
                        onClick={() => setReassignOpen(true)}
                        className="text-[11px] font-semibold text-[#84ab4a] hover:text-[#6d9040] transition-colors"
                      >
                        Change Associate
                      </button>
                    )}
                  </div>
                  {associateRepCode || lead.referrerAgentName ? (
                    <div className="text-sm text-gray-700">
                      <span className="font-mono font-semibold text-gray-800">{associateRepCode}</span>
                      {lead.referrerAgentName && <span className="text-gray-500 ml-2">{lead.referrerAgentName}</span>}
                      {lead.referrerAgentEmail && <div className="text-[11px] text-gray-400 mt-0.5">{lead.referrerAgentEmail}</div>}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">No associate assigned</div>
                  )}

                  {reassignOpen && (
                    <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
                      {!reassignConfirm ? (
                        <>
                          <div>
                            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                              Select New Associate
                            </label>
                            {repsQuery.isLoading ? (
                              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading associates…
                              </div>
                            ) : (
                              <select
                                value={selectedRepCode}
                                onChange={(e) => setSelectedRepCode(e.target.value)}
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                              >
                                <option value="">— Choose associate —</option>
                                {(repsQuery.data ?? [])
                                  .filter((r) => r.status === "active" && r.repCode !== associateRepCode)
                                  .map((r) => (
                                    <option key={r.repCode} value={r.repCode!}>
                                      {r.repCode} — {r.firstName} {r.lastName ?? ""}
                                    </option>
                                  ))}
                              </select>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              disabled={!selectedRepCode}
                              onClick={() => setReassignConfirm(true)}
                              className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#84ab4a] text-white hover:bg-[#6d9040] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              Continue
                            </button>
                            <button
                              onClick={() => { setReassignOpen(false); setSelectedRepCode(""); }}
                              className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="text-xs font-bold text-amber-700 mb-1">Confirm Reassignment</div>
                            <p className="text-xs text-amber-700 leading-relaxed">
                              This will move <strong>{fullName}</strong> from{" "}
                              <strong>{associateRepCode ?? "unassigned"}</strong> to{" "}
                              <strong>{selectedRepCode}</strong> moving forward. Existing notes, documents, portal links, and history will be preserved.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              disabled={reassignMutation.isPending}
                              onClick={() => reassignMutation.mutate({ leadId: lead.id, newRepCode: selectedRepCode, teamMemberId })}
                              className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                            >
                              {reassignMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                              Confirm Reassignment
                            </button>
                            <button
                              onClick={() => setReassignConfirm(false)}
                              className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                              Back
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Application / Booking Details — Fundable360 only */}
              {showF360Card && (
                <div className="border border-[#84ab4a]/25 rounded-xl p-4 bg-[#84ab4a]/5">
                  <div className="text-xs font-bold text-[#84ab4a] uppercase tracking-wider mb-3">
                    {lead.verticalName ? `${lead.verticalName} Application` : "Booking Form Details"}
                  </div>

                  {/* Associate Details */}
                  {(associateRepCode || lead.referrerAgentName || lead.referrerAgentEmail) && (
                    <div className="mb-3">
                      <div className="text-[10px] font-bold text-[#84ab4a]/70 uppercase tracking-wider mb-1.5">Associate Details</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {[
                          ["Associate Name",  lead.referrerAgentName],
                          ["Rep Code",        associateRepCode],
                          ["Associate Email", lead.referrerAgentEmail],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label as string} className="bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                            <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submission Answers */}
                  <div className="text-[10px] font-bold text-[#84ab4a]/70 uppercase tracking-wider mb-1.5">Submission Answers</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      ["Business Name", f360BusinessName],
                      ["Industry / Type of Business", f360Industry],
                      ["First Name Last Name", fullName],
                      ["Email Address", lead.email],
                      ["Phone Number", lead.phone],
                      ["How long have you been in business?", f360TimeInBusiness],
                      ["Current Estimated Monthly Revenue", f360Revenue],
                      ["What best describes your business credit status?", f360CreditStatus],
                      ["What is your main goal with Fundable360?", f360MainGoal],
                      ["Additional Information", f360AdditionalInfo],
                      ["I agree to receive SMS and email communications", f360SmsEmailConsent],
                      ["I agree to receive updates, training invites", f360MarketingConsent],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label as string} className="bg-white rounded-lg p-2.5">
                        <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                        <div className="text-sm font-medium text-gray-800">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Legacy360 Application Details — show for any Legacy360 lead */}
              {lead.verticalName === "Legacy360" && (() => {
                // Which of the three Legacy360 application types this lead is.
                // Resolved as ONE value in priority order rather than as independent
                // booleans. Previously isAdvantage and isL360Plan125 were computed
                // separately and could both be true, so a lead whose GHL contact
                // carried both field sets (one person submitting both forms dedupes
                // to a single GHL contact) rendered the Agent Expansion answers and
                // the Savings Analysis answers stacked under one heading.
                //
                // l360LeadType is the authoritative signal: the pull-sync derives it
                // from the pipeline ID. form_name and page_url are fallbacks for leads
                // that arrived by webhook before a sync stamped the type, and the
                // 125-Plan field check is a last resort for older rows.
                // "lagecy360_advantage" is the intentional misspelling matching the
                // GHL workflow payload.
                const resolvedSubtype = resolveLegacy360Subtype(lead);
                const l360Kind: "advantage" | "125plan" | "living_trust" =
                  resolvedSubtype === "advantage" || resolvedSubtype === "125plan"
                    ? resolvedSubtype
                    : "living_trust";

                const isAdvantage = l360Kind === "advantage";
                const isL360Plan125 = l360Kind === "125plan";

                // Parse a stored JSON array (or plain string) into a display-friendly string.
                const fmtArr = (v: string | null | undefined): string | null => {
                  if (!v) return null;
                  try {
                    const parsed = JSON.parse(v);
                    if (Array.isArray(parsed)) return parsed.join(", ");
                  } catch { /* not JSON */ }
                  return v;
                };

                return (
                  <div className="border border-[#C8920A]/25 rounded-xl p-4 bg-[#C8920A]/5">
                    <div className="text-xs font-bold text-[#C8920A] uppercase tracking-wider mb-3">
                      {LEGACY360_SUBTYPE_CONFIG[l360Kind].formName}
                    </div>

                    {/* Referring Associate Details */}
                    {(lead.repCode || lead.referrerAgentName || lead.referrerAgentEmail) && (
                      <div className="mb-3">
                        <div className="text-[10px] font-bold text-[#C8920A]/70 uppercase tracking-wider mb-1.5">Referring Associate</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ["Associate Name",  lead.referrerAgentName],
                            ["Rep Code",        lead.repCode],
                            ["Associate Email", lead.referrerAgentEmail],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label as string} className="bg-white rounded-lg p-2.5">
                              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                              <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contact Details */}
                    <div className="mb-3">
                      <div className="text-[10px] font-bold text-[#C8920A]/70 uppercase tracking-wider mb-1.5">
                        {isAdvantage ? "Agent Details" : "Client Details"}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {[
                          ["Full Name",  [lead.firstName, lead.lastName].filter(Boolean).join(" ") || null],
                          ["Email",      lead.email],
                          ["Phone",      lead.phone],
                          ...(isL360Plan125 ? [["Business Name", lead.businessName]] : []),
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label as string} className="bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                            <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Advantage-specific fields */}
                    {isAdvantage && (lead.l360CityState || lead.l360CurrentlyLicensed || lead.l360LicenseTypes || lead.l360GoodStanding || lead.l360IndustryExperience || lead.l360CurrentRole || lead.l360ContractedWithAgency || lead.l360AgencyType || lead.l360LeadSources || lead.l360LookingFor || lead.l360LicenseRevoked || lead.l360WillingBackgroundCheck || lead.l360WillingToContract) && (
                      <>
                        <div className="text-[10px] font-bold text-[#C8920A]/70 uppercase tracking-wider mb-1.5 mt-3">Agent Expansion Details</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ["City / State",              lead.l360CityState],
                            ["Currently Licensed",        lead.l360CurrentlyLicensed],
                            ["License Types",             fmtArr(lead.l360LicenseTypes)],
                            ["Good Standing",             lead.l360GoodStanding],
                            ["Industry Experience",       lead.l360IndustryExperience],
                            ["Current Role",              lead.l360CurrentRole],
                            ["Contracted with Agency",    lead.l360ContractedWithAgency],
                            ["Agency Type",               lead.l360AgencyType],
                            ["Lead Sources",              fmtArr(lead.l360LeadSources)],
                            ["Looking For",               fmtArr(lead.l360LookingFor)],
                            ["License Revoked",           lead.l360LicenseRevoked],
                            ["Willing — Background Check",lead.l360WillingBackgroundCheck],
                            ["Willing to Contract",       lead.l360WillingToContract],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label as string} className="bg-white rounded-lg p-2.5">
                              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                              <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* 125 Plan-specific fields */}
                    {isL360Plan125 && (lead.l360Plan125NumEmployees || lead.l360Plan125NumW2Employees || lead.l360Plan125PayrollProvider || lead.l360Plan125HasHealthIns || lead.l360Plan125PayrollReportUrl) && (
                      <>
                        <div className="text-[10px] font-bold text-[#C8920A]/70 uppercase tracking-wider mb-1.5 mt-3">Savings Analysis Details</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ["Number of Employees",       lead.l360Plan125NumEmployees],
                            ["Number of W-2 Employees",   lead.l360Plan125NumW2Employees],
                            ["Current Payroll Provider",  lead.l360Plan125PayrollProvider],
                            ["Offers Health Insurance",   lead.l360Plan125HasHealthIns],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label as string} className="bg-white rounded-lg p-2.5">
                              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                              <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                            </div>
                          ))}
                        </div>
                        {lead.l360Plan125PayrollReportUrl && (
                          <div className="mt-2 bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Payroll Report</div>
                            <a href={lead.l360Plan125PayrollReportUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#C8920A] hover:underline">
                              View uploaded report
                            </a>
                          </div>
                        )}
                      </>
                    )}

                    {/* Living Trust fields — shown for non-Advantage, non-125-Plan leads */}
                    {!isAdvantage && !isL360Plan125 && (lead.l360LeadType || lead.l360State || lead.l360ExistingPlan || lead.l360TrustGoal || lead.l360PlanningType || lead.l360RealEstate || lead.l360AssetValue || lead.l360HasBeneficiaries || lead.l360Timeline || lead.l360PreferredContact || lead.l360QuestionsComments || lead.l360ConsentToContact) && (
                      <>
                        <div className="text-[10px] font-bold text-[#C8920A]/70 uppercase tracking-wider mb-1.5 mt-3">Submitted Information</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ["Service Interest",  lead.l360LeadType],
                            ["State",             lead.l360State],
                            ["Planning Type",     lead.l360PlanningType],
                            ["Existing Plan",     lead.l360ExistingPlan],
                            ["Real Estate",       lead.l360RealEstate],
                            ["Asset Value",       lead.l360AssetValue],
                            ["Has Beneficiaries", lead.l360HasBeneficiaries],
                            ["Timeline",          lead.l360Timeline],
                            ["Preferred Contact", lead.l360PreferredContact],
                            ["Consent",           lead.l360ConsentToContact],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label as string} className="bg-white rounded-lg p-2.5">
                              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                              <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                            </div>
                          ))}
                        </div>
                        {lead.l360TrustGoal && (
                          <div className="mt-2 bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Trust Goal</div>
                            <div className="text-sm text-gray-800">{lead.l360TrustGoal}</div>
                          </div>
                        )}
                        {lead.l360QuestionsComments && (
                          <div className="mt-2 bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Questions / Comments</div>
                            <div className="text-sm text-gray-800">{lead.l360QuestionsComments}</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* ── Scale360 Audit Submission — from Wibiz scorecard webhook (s360Audit* fields) ── */}
              {showS360Card && !!(lead.s360AuditScore || lead.s360AuditStatus || lead.s360LossPerYear || lead.s360PlanName || lead.s360IndustryName || lead.s360EconomyTier || lead.s360PdfLink || lead.s360SavedHours || (lead as any).s360GhostPerYear || (lead as any).s360IndustryBenefits) && (() => {
                const fmtVal = (v: string | null | undefined): string | null => {
                  if (!v) return null;
                  const s = String(v);
                  if (s.startsWith("$")) return s;
                  const n = parseFloat(s.replace(/,/g, ""));
                  if (!isNaN(n) && /^\d/.test(s)) return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
                  return s;
                };
                return (
                  <div className="border border-[#E8620A]/25 rounded-xl overflow-hidden">
                    <div className="bg-[#E8620A]/8 px-4 py-3 border-b border-[#E8620A]/15 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-[#E8620A] uppercase tracking-wider">Scale360 Audit Submission</div>
                        {lead.s360AuditStatus && <div className="text-[10px] text-gray-500 mt-0.5">{lead.s360AuditStatus}</div>}
                      </div>
                      {lead.s360PdfLink && (
                        <a href={lead.s360PdfLink} target="_blank" rel="noopener noreferrer"
                          className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#E8620A] text-white hover:bg-[#c94e08] transition-colors">
                          <FileText className="w-3 h-3" /> Audit Report
                        </a>
                      )}
                    </div>
                    <div className="p-4 space-y-4 bg-[#E8620A]/3">
                      {(lead.s360IndustryName || lead.s360EconomyTier || lead.s360PersonaId || lead.s360Multiplier) && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Client Profile</div>
                          <div className="grid grid-cols-2 gap-2">
                            {([
                              { label: "Industry",          value: lead.s360IndustryName },
                              { label: "Economy Tier",      value: lead.s360EconomyTier },
                              { label: "Client Persona",    value: lead.s360PersonaId },
                              { label: "Complexity Factor", value: lead.s360Multiplier },
                            ] as { label: string; value: string | null | undefined }[])
                              .filter(r => r.value)
                              .map(({ label, value }) => (
                                <div key={label} className="bg-white rounded-lg p-2.5 border border-gray-100">
                                  <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                                  <div className="text-sm font-semibold text-gray-800">{value}</div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      {(lead.s360AuditScore || lead.s360SavedHours) && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Performance</div>
                          <div className="grid grid-cols-2 gap-2">
                            {lead.s360AuditScore && (
                              <div className="col-span-2 bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Operational Efficiency Score</div>
                                <div className="text-xl font-bold text-[#E8620A]">{lead.s360AuditScore}<span className="text-sm font-normal text-gray-400">/100</span></div>
                                <div className="text-[10px] text-gray-400 mt-0.5">Based on Wibiz scorecard responses. Score below 70 signals significant automation opportunity.</div>
                              </div>
                            )}
                            {lead.s360SavedHours && (
                              <div className="bg-white rounded-lg p-2.5 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Est. Hours Saved / Year</div>
                                <div className="text-sm font-semibold text-gray-800">{lead.s360SavedHours}</div>
                                <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">Projected annual time savings from implementing Scale360.</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {(lead.s360LossPerYear || lead.s360LossPerMonth || (lead as any).s360GhostPerYear || (lead as any).s360AfterPerYear || (lead as any).s360TimePerYear || lead.s360Roi || lead.s360BreakevenMonths) && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Revenue Impact</div>
                          <div className="grid grid-cols-2 gap-2">
                            {([
                              { label: "Annual Revenue Leakage",   value: fmtVal(lead.s360LossPerYear),             desc: "Estimated revenue lost annually due to current inefficiencies." },
                              { label: "Monthly Revenue Leakage",  value: fmtVal(lead.s360LossPerMonth),            desc: "Estimated monthly revenue impact from current process gaps." },
                              { label: "Annual Hidden Cost",        value: fmtVal((lead as any).s360GhostPerYear),  desc: "Hidden costs not immediately visible but impacting the bottom line." },
                              { label: "Revenue After Scale360",    value: fmtVal((lead as any).s360AfterPerYear),  desc: "Projected annual revenue position after Scale360 implementation." },
                              { label: "Annual Time Savings Value", value: fmtVal((lead as any).s360TimePerYear),   desc: "Dollar value of time recovered each year." },
                              { label: "Projected ROI",             value: lead.s360Roi ? (String(lead.s360Roi).includes('%') ? lead.s360Roi : `+${lead.s360Roi}%`) : null, desc: "Return on investment expected from Scale360 adoption." },
                              { label: "Break-Even Period",         value: lead.s360BreakevenMonths ? `${lead.s360BreakevenMonths} ${Number(lead.s360BreakevenMonths) === 1 ? 'month' : 'months'}` : null, desc: "Time to recover the Scale360 investment." },
                            ] as { label: string; value: string | null | undefined; desc: string }[])
                              .filter(r => r.value)
                              .map(({ label, value, desc }) => (
                                <div key={label} className="bg-white rounded-lg p-2.5 border border-gray-100">
                                  <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                                  <div className="text-sm font-bold text-[#E8620A]">{value}</div>
                                  <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">{desc}</div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      {(lead.s360PlanName || lead.s360PlanDesc || lead.s360PlanPrice) && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Recommended Plan</div>
                          <div className="bg-white rounded-xl p-4 border border-[#E8620A]/20">
                            {lead.s360PlanName && <div className="font-bold text-base text-gray-900 mb-2">{lead.s360PlanName}</div>}
                            {lead.s360PlanPrice && <div className="text-sm font-semibold text-[#E8620A] mb-2">{lead.s360PlanPrice}/mo</div>}
                            {lead.s360PlanDesc && <p className="text-xs text-gray-600 leading-relaxed">{lead.s360PlanDesc}</p>}
                          </div>
                        </div>
                      )}
                      {(lead.s360IndustryInsight || (lead as any).s360IndustryBenefits) && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Industry Insights</div>
                          <div className="space-y-2">
                            {lead.s360IndustryInsight && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Industry Overview</div>
                                <div className="text-xs text-gray-700 leading-relaxed">{lead.s360IndustryInsight}</div>
                              </div>
                            )}
                            {(lead as any).s360IndustryBenefits && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Industry Benefits</div>
                                <div className="text-xs text-gray-700 leading-relaxed">{(lead as any).s360IndustryBenefits}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ── Scale360 Analysis Report — from analysis intake form (s360Analysis* fields) ── */}
              {showS360AnalysisCard && (() => {
                const fmtVal = (v: string | null | undefined): string | null => {
                  if (!v) return null;
                  const s = String(v);
                  if (s.startsWith("$")) return s;
                  const n = parseFloat(s.replace(/,/g, ""));
                  if (!isNaN(n) && /^\d/.test(s)) return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
                  return s;
                };

                const planName     = lead.s360AnalysisPlanName || lead.s360AnalysisRecommendedPlan;
                const keyFeatures  = lead.s360AnalysisPlanKeyFeatures ? lead.s360AnalysisPlanKeyFeatures.split(", ").filter(Boolean) : [];
                const assumptions  = lead.s360AnalysisPlanAssumptions ? lead.s360AnalysisPlanAssumptions.split(" | ").filter(Boolean) : [];
                let clarQuestions: Array<{ question: string; field: string; priority: string; options: string[] }> = [];
                try { if (lead.s360AnalysisClarificationQuestions) clarQuestions = JSON.parse(lead.s360AnalysisClarificationQuestions); } catch {}

                const s360Ghl = ((lead as any).metadata as any)?.s360Ghl ?? {};
                const hasBizProfile = !!(lead.s360AnalysisExtractedIndustry || lead.s360AnalysisExtractedTeamSize || lead.s360AnalysisExtractedRevenue || lead.s360AnalysisChannels);
                const hasFinancial  = !!(lead.s360AnalysisMonthlyInefficiency || lead.s360AnalysisMonthlyTimeCost || lead.s360AnalysisMissedOpportunity || lead.s360AnalysisMonthlySavings || lead.s360AnalysisHoursRecovered);
                const hasPlan       = !!(planName || lead.s360AnalysisPlanMonthlyPrice);
                const hasContext    = !!(lead.s360AnalysisBusinessSummary || lead.s360AnalysisBizDesc || lead.s360AnalysisPainPoints || lead.s360AnalysisFutureNarrative);

                return (
                  <div className="border border-[#E8620A]/25 rounded-xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-[#E8620A]/8 px-4 py-3 border-b border-[#E8620A]/15">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-[#E8620A] uppercase tracking-wider">Scale360 Analysis Report</div>
                          {lead.s360AnalysisReportGeneratedAt && (
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              Generated: {(() => { try { return new Date(lead.s360AnalysisReportGeneratedAt!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return lead.s360AnalysisReportGeneratedAt; } })()}
                            </div>
                          )}
                          {lead.s360AnalysisSessionId && (
                            <div className="text-[10px] text-gray-400 mt-0.5 font-mono">Session: {lead.s360AnalysisSessionId.slice(0, 8)}…</div>
                          )}
                        </div>
                        {lead.s360AnalysisPdfUrl && (
                          <a href={lead.s360AnalysisPdfUrl} target="_blank" rel="noopener noreferrer"
                            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#E8620A] text-white hover:bg-[#c94e08] transition-colors">
                            <FileText className="w-3 h-3" /> View Report
                          </a>
                        )}
                      </div>
                      {lead.s360AnalysisReportFileName && (
                        <div className="text-[10px] text-gray-400 mt-1">{lead.s360AnalysisReportFileName}</div>
                      )}
                    </div>

                    <div className="p-4 space-y-5 bg-[#E8620A]/3">

                      {/* 1 · Business Profile */}
                      {hasBizProfile && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Business Profile</div>
                          <div className="grid grid-cols-2 gap-2">
                            {([
                              { label: "Industry",        value: lead.s360AnalysisExtractedIndustry },
                              { label: "Team Size",        value: lead.s360AnalysisExtractedTeamSize },
                              { label: "Monthly Revenue",  value: fmtVal(lead.s360AnalysisExtractedRevenue) },
                              { label: "Active Channels",  value: lead.s360AnalysisChannels },
                            ] as { label: string; value: string | null | undefined }[])
                              .filter(r => r.value)
                              .map(({ label, value }) => (
                                <div key={label} className="bg-white rounded-lg p-2.5 border border-gray-100">
                                  <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                                  <div className="text-sm font-semibold text-gray-800">{value}</div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* 2 · Operational Efficiency Score */}
                      {lead.s360AnalysisProductivityScore && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Operational Efficiency Score</div>
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <div className="flex items-end gap-2 mb-1.5">
                              <span className="text-3xl font-black text-[#E8620A]">{lead.s360AnalysisProductivityScore}</span>
                              <span className="text-base text-gray-400 mb-0.5">/100</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2">
                              <div className="h-1.5 rounded-full bg-[#E8620A]" style={{ width: `${Math.min(parseInt(lead.s360AnalysisProductivityScore ?? "0") || 0, 100)}%` }} />
                            </div>
                            <div className="text-[10px] text-gray-500 leading-relaxed">
                              A score of {lead.s360AnalysisProductivityScore}/100 means the business currently has significant operational gaps. When talking to the client, frame this as untapped potential — not a failing grade. Every point below 100 is recoverable revenue.
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 3 · Financial Impact Analysis */}
                      {hasFinancial && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Financial Impact Analysis</div>
                          <div className="grid grid-cols-2 gap-2">
                            {([
                              { label: "Monthly Inefficiency Cost",        value: fmtVal(lead.s360AnalysisMonthlyInefficiency), desc: "Total monthly cost of operational gaps — missed follow-ups, slow responses, and manual tasks the system can handle." },
                              { label: "Monthly Time Cost",                value: fmtVal(lead.s360AnalysisMonthlyTimeCost),     desc: "Dollar value of staff hours spent on tasks Scale360 can automate." },
                              { label: "Missed Revenue Opportunity",       value: fmtVal(lead.s360AnalysisMissedOpportunity),  desc: "Revenue this business could capture each month with better response systems and automated follow-up." },
                              { label: "Est. Monthly Savings w/ Scale360", value: fmtVal(lead.s360AnalysisMonthlySavings),     desc: "Projected monthly savings after Scale360 is live — based on their current inefficiency profile and team size." },
                              { label: "Hours Recovered Per Month",        value: lead.s360AnalysisHoursRecovered ? `${lead.s360AnalysisHoursRecovered} hrs` : null, desc: "Staff hours freed up each month by automating customer communication, bookings, and follow-up workflows." },
                            ] as { label: string; value: string | null | undefined; desc: string }[])
                              .filter(r => r.value)
                              .map(({ label, value, desc }) => (
                                <div key={label} className="bg-white rounded-lg p-2.5 border border-gray-100">
                                  <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                                  <div className="text-sm font-bold text-[#E8620A]">{value}</div>
                                  <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">{desc}</div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* 4 · Recommended Plan */}
                      {hasPlan && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Recommended Scale360 Plan</div>
                          <div className="bg-white rounded-xl p-4 border border-[#E8620A]/20 space-y-3">
                            {planName && <div className="font-bold text-base text-gray-900">{planName}</div>}
                            {(lead.s360AnalysisPlanMonthlyPrice || lead.s360AnalysisPlanAnnualPrice || lead.s360AnalysisPlanSetupFee) && (
                              <div className="flex flex-wrap gap-3 items-baseline">
                                {lead.s360AnalysisPlanMonthlyPrice && <span className="text-base font-bold text-[#E8620A]">{lead.s360AnalysisPlanMonthlyPrice}<span className="text-xs font-normal text-gray-400">/mo</span></span>}
                                {lead.s360AnalysisPlanAnnualPrice   && <span className="text-xs text-gray-500">{lead.s360AnalysisPlanAnnualPrice}/yr</span>}
                                {lead.s360AnalysisPlanSetupFee       && <span className="text-xs text-gray-500">{lead.s360AnalysisPlanSetupFee} setup</span>}
                              </div>
                            )}
                            {keyFeatures.length > 0 && (
                              <div>
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1.5">Key Features</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {keyFeatures.map((f: string, i: number) => (
                                    <span key={i} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E8620A]/10 text-[#E8620A] border border-[#E8620A]/20 capitalize">{f}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {lead.s360AnalysisPlanReasoning && (
                              <div>
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Why This Plan</div>
                                <div className="space-y-1">
                                  {lead.s360AnalysisPlanReasoning.split(" | ").filter(Boolean).map((r: string, i: number) => (
                                    <div key={i} className="flex items-start gap-1.5">
                                      <span className="text-[#E8620A] text-xs mt-0.5 flex-shrink-0">•</span>
                                      <span className="text-[10px] text-gray-600 leading-relaxed">{r}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {assumptions.length > 0 && (
                              <div>
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Plan Assumptions</div>
                                <div className="text-[10px] text-gray-400 italic mb-1">The AI made these assumptions to arrive at this recommendation. Verify them on your discovery call.</div>
                                <div className="space-y-1">
                                  {assumptions.map((a: string, i: number) => (
                                    <div key={i} className="flex items-start gap-1.5">
                                      <span className="text-gray-300 text-xs mt-0.5 flex-shrink-0">–</span>
                                      <span className="text-[10px] text-gray-500 leading-relaxed">{a}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 5 · Business Context */}
                      {hasContext && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Business Context</div>
                          <div className="space-y-2">
                            {lead.s360AnalysisBusinessSummary && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Business Summary</div>
                                <div className="text-xs text-gray-700 leading-relaxed">{lead.s360AnalysisBusinessSummary}</div>
                              </div>
                            )}
                            {(lead.s360AnalysisBizDesc && !lead.s360AnalysisBusinessSummary) && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Business Description</div>
                                <div className="text-xs text-gray-700 leading-relaxed">{lead.s360AnalysisBizDesc}</div>
                              </div>
                            )}
                            {lead.s360AnalysisPainPoints && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Key Challenges Identified</div>
                                <div className="text-[10px] text-gray-400 italic mb-1.5">These are the specific pain points Scale360 is designed to solve. Use them to make the pitch personal.</div>
                                <div className="text-xs text-gray-700 leading-relaxed">{lead.s360AnalysisPainPoints}</div>
                              </div>
                            )}
                            {lead.s360AnalysisFutureNarrative && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Future State with Scale360</div>
                                <div className="text-[10px] text-gray-400 italic mb-1.5">This is the AI-generated vision for this business after Scale360 is live. Read it before your call — it's your closing story.</div>
                                <div className="text-xs text-gray-700 leading-relaxed">{lead.s360AnalysisFutureNarrative}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 6 · GHL Enrichment — form type, vertical, refinement data */}
                      {(s360Ghl.s360GhlFormType || s360Ghl.s360GhlBusinessVertical || s360Ghl.s360GhlRefinedAt || s360Ghl.s360GhlRefinementAnswers || s360Ghl.s360GhlRefinedInsights) && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-2">Enrichment & Refinement</div>
                          <div className="space-y-2">
                            {(s360Ghl.s360GhlFormType || s360Ghl.s360GhlBusinessVertical) && (
                              <div className="flex flex-wrap gap-2">
                                {s360Ghl.s360GhlFormType && (
                                  <div className="bg-white rounded-lg p-2.5 border border-gray-100 flex-1 min-w-[120px]">
                                    <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Form Type</div>
                                    <div className="text-sm font-semibold text-gray-800">{s360Ghl.s360GhlFormType}</div>
                                  </div>
                                )}
                                {s360Ghl.s360GhlBusinessVertical && (
                                  <div className="bg-white rounded-lg p-2.5 border border-gray-100 flex-1 min-w-[120px]">
                                    <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Business Vertical</div>
                                    <div className="text-sm font-semibold text-gray-800">{s360Ghl.s360GhlBusinessVertical}</div>
                                  </div>
                                )}
                                {s360Ghl.s360GhlRefinedAt && (
                                  <div className="bg-white rounded-lg p-2.5 border border-gray-100 flex-1 min-w-[120px]">
                                    <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Refined At</div>
                                    <div className="text-sm font-semibold text-gray-800">{(() => { try { return new Date(s360Ghl.s360GhlRefinedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return s360Ghl.s360GhlRefinedAt; } })()}</div>
                                  </div>
                                )}
                              </div>
                            )}
                            {s360Ghl.s360GhlRefinementAnswers && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Refinement Answers</div>
                                <div className="text-[10px] text-gray-400 italic mb-1.5">Associate's answers to the AI-generated follow-up questions. Used to sharpen the analysis.</div>
                                <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">{s360Ghl.s360GhlRefinementAnswers}</div>
                              </div>
                            )}
                            {s360Ghl.s360GhlRefinedInsights && (
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Refined Insights</div>
                                <div className="text-[10px] text-gray-400 italic mb-1.5">AI-updated analysis after refinement questions were answered. This supersedes the original pain points.</div>
                                <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">{s360Ghl.s360GhlRefinedInsights}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 7 · Follow-Up Questions */}
                      {clarQuestions.length > 0 && (
                        <div>
                          <div className="text-[9px] font-bold text-[#E8620A]/60 uppercase tracking-widest mb-1">Recommended Follow-Up Questions</div>
                          <div className="text-[10px] text-gray-400 mb-2">AI-generated questions to help you qualify and close this lead. Cover HIGH priority questions on your first call.</div>
                          <div className="space-y-2">
                            {[...clarQuestions].sort((a, b) => a.priority === "high" ? -1 : 1).map((q, i) => (
                              <div key={i} className="bg-white rounded-lg p-3 border border-gray-100">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${q.priority === "high" ? "bg-red-50 text-red-500" : "bg-gray-50 text-gray-400"}`}>
                                    {q.priority === "high" ? "High Priority" : "Medium Priority"}
                                  </span>
                                </div>
                                <div className="text-xs font-medium text-gray-800 mb-1.5">{q.question}</div>
                                {q.options.length > 0 && (
                                  <div className="space-y-0.5">
                                    {q.options.map((opt: string, j: number) => (
                                      <div key={j} className="text-[10px] text-gray-500 flex items-start gap-1">
                                        <span className="text-gray-300 flex-shrink-0">○</span>
                                        <span>{opt}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })()}

              {/* Share360 Application Details */}
              {showSh360Card && (
                <div className="border border-[#1A6FD4]/25 rounded-xl p-4 bg-[#1A6FD4]/5">
                  <div className="text-xs font-bold text-[#1A6FD4] uppercase tracking-wider mb-3">
                    Share360 Application
                    {lead.sh360Role && <span className="ml-2 font-normal normal-case text-[#1A6FD4]/70">— {lead.sh360Role.charAt(0).toUpperCase() + lead.sh360Role.slice(1)}</span>}
                  </div>

                  {/* Section A: Associate / Referral Details */}
                  {(lead.repCode || lead.referrerAgentName || lead.referrerAgentEmail) && (
                    <div className="mb-3">
                      <div className="text-[10px] font-bold text-[#1A6FD4]/70 uppercase tracking-wider mb-1.5">Associate Details</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {[
                          ["Rep Code",       lead.repCode],
                          ["Referred By",    (lead as any).referrerAgentName],
                          ["Referrer Email", (lead as any).referrerAgentEmail],
                          ["Vertical",       lead.verticalName],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label as string} className="bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                            <div className="text-sm text-gray-800 font-medium">{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section B: Submission / Intake Answers */}
                  <div>
                    <div className="text-[10px] font-bold text-[#1A6FD4]/70 uppercase tracking-wider mb-1.5">Submission Answers</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {[
                        ...(() => {
                          const meta = lead.metadata as Record<string, unknown> | null | undefined;
                          const metaOrg = meta ? String(meta["SH360 Org Name"] || meta["SH360 Business Name"] || meta["Organization Name"] || meta["Business Name"] || meta["Org Name"] || "").trim() : "";
                          if (isShare360Business) {
                            return [
                              ["Supporting Nonprofit Name", getMetaString(meta, "business_supporting_nonprofit")],
                              ["Your Name", getMetaString(meta, "business_contact_name") || fullName],
                              ["Business Name", (lead as any).sh360BusinessName || metaOrg || lead.businessName],
                              ["Best Email", lead.email || getMetaString(meta, "email")],
                              ["Best Phone Number", lead.phone || getMetaString(meta, "phone")],
                              ["How does your business operate?", getMetaString(meta, "business_operation")],
                              ["Rough monthly card volume?", getMetaString(meta, "business_card_volume") || lead.sh360MonthlyProcessing],
                              ["Current processor (if you know it)", getMetaString(meta, "business_processor")],
                              ["I'd like to support this nonprofit through the program", getMetaString(meta, "business_interest") || lead.sh360PrimaryGoal],
                              ["Anything you'd like us to know?", getMetaString(meta, "business_notes") || lead.sh360QuestionsComments],
                            ];
                          }
                          if (isShare360Nonprofit) {
                            return [
                              ["Organization Name", (lead as any).sh360OrgName || metaOrg || lead.businessName || getMetaString(meta, "nonprofit_organization_name", "org_name")],
                              ["Website URL", getMetaString(meta, "nonprofit_website")],
                              ["Primary Contact Name", getMetaString(meta, "nonprofit_primary_contact_name") || fullName],
                              ["Title/Role", getMetaString(meta, "nonprofit_title_role") || lead.sh360JobTitle],
                              ["Email Address", getMetaString(meta, "nonprofit_email") || lead.email],
                              ["Phone Number", getMetaString(meta, "nonprofit_phone") || lead.phone],
                              ["City/State", getMetaString(meta, "nonprofit_city_state") || lead.sh360CityRegion || lead.sh360State],
                              ["Approximately how many individuals do you impact annually?", getMetaString(meta, "nonprofit_annual_impact") || lead.sh360AnnualDonations],
                              ["Who do you primarily serve?", getMetaString(meta, "nonprofit_who_served")],
                              ["What are the main programs or services you provide?", getMetaString(meta, "nonprofit_programs_services")],
                              ["What are your primary sources of funding today?", getMetaString(meta, "nonprofit_funding_sources")],
                              ["What is your approximate annual budget or fundraising goal?", getMetaString(meta, "nonprofit_budget_goal")],
                              ["Are you currently working with any fundraising platforms or programs?", getMetaString(meta, "nonprofit_current_platforms")],
                              ["Do you currently have relationships with local business owners?", getMetaString(meta, "nonprofit_business_relationships")],
                              ["If yes, approximately how many active business relationships do you have?", getMetaString(meta, "nonprofit_active_relationship_count")],
                              ["Do you have someone on your team who could serve as a program ambassador?", getMetaString(meta, "nonprofit_ambassador_available")],
                              ["Have you reviewed how the Share360 program works?", getMetaString(meta, "nonprofit_reviewed_program")],
                              ["How soon are you looking to implement a new fundraising solution?", getMetaString(meta, "nonprofit_implementation_timeline")],
                              ["What interests you most about the Share360 program?", getMetaString(meta, "nonprofit_share360_interest") || lead.sh360PrimaryGoal],
                              ["Is there anything else you'd like us to know before the consultation?", getMetaString(meta, "nonprofit_additional_notes") || lead.sh360QuestionsComments],
                            ];
                          }
                          return [
                            ["Organization Name", (lead as any).sh360OrgName || (lead as any).sh360BusinessName || metaOrg || lead.businessName],
                            ["Best Email", lead.email],
                            ["Best Phone Number", lead.phone],
                            ["Interest Type", lead.sh360InterestType],
                            ["Consent to Contact", lead.sh360ConsentToContact],
                          ];
                        })(),
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label as string} className="bg-white rounded-lg p-2.5">
                          <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                          <div className="text-sm text-gray-800 font-medium">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Long-text fields rendered below the grid */}
                  {!isShare360Business && !isShare360Nonprofit && lead.sh360PrimaryGoal && (
                    <div className="mt-2 bg-white rounded-lg p-2.5">
                      <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Primary Goal</div>
                      <div className="text-sm text-gray-800">{lead.sh360PrimaryGoal}</div>
                    </div>
                  )}
                  {!isShare360Business && !isShare360Nonprofit && lead.sh360QuestionsComments && (
                    <div className="mt-2 bg-white rounded-lg p-2.5">
                      <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Questions / Comments</div>
                      <div className="text-sm text-gray-800">{lead.sh360QuestionsComments}</div>
                    </div>
                  )}
                  {!isShare360Business && !isShare360Nonprofit && lead.sh360PageUrl && (
                    <div className="mt-2 bg-white rounded-lg p-2.5">
                      <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Source Page</div>
                      <div className="text-xs text-blue-600 break-all">{lead.sh360PageUrl}</div>
                    </div>
                  )}
                  {/* Extra fields from metadata catch-all not already shown via sh360* columns */}
                  {lead.metadata && !isShare360Business && !isShare360Nonprofit && (() => {
                    // Normalise key → strip underscores, lowercase — catches snake_case,
                    // "Spaced Title", "MIXED ID" and every other GHL key format in one pass.
                    const normKey = (k: string) => k.replace(/_/g, " ").toLowerCase();
                    const SH360_SUPPRESS_NORM = new Set([
                      // Routing / meta
                      "lead type","role","vertical","source","submission type",
                      "route pipeline key","route pipeline name",
                      "utm source","utm medium","utm campaign",
                      "page title","page url","form name",
                      "source page","timestamp",
                      // Identity (already in contact header)
                      "first name","last name","full name","email","phone",
                      "business contact name","nonprofit primary contact name",
                      "business email","business phone","nonprofit email","nonprofit phone",
                      // sh360* column fields — old names
                      "business name","org name","industry",
                      "monthly processing","supports nonprofit","tax status",
                      "annual donations","supporters count","works with","network size",
                      "city region","state","job title","interest type","primary goal",
                      "questions comments","consent to contact",
                      // sh360* column fields — new prefixed names
                      "nonprofit organization name","business card volume",
                      "business supporting nonprofit","nonprofit annual impact",
                      "nonprofit city state","nonprofit title role",
                      "business interest","nonprofit share360 interest",
                      "business notes","nonprofit additional notes",
                      "business operation","business processor",
                      "nonprofit website","nonprofit primary contact name",
                      "nonprofit email","nonprofit phone",
                      "nonprofit who served","nonprofit programs services",
                      "nonprofit funding sources","nonprofit budget goal",
                      "nonprofit current platforms","nonprofit business relationships",
                      "nonprofit active relationship count","nonprofit ambassador available",
                      "nonprofit reviewed program","nonprofit implementation timeline",
                      // Questionnaire stored as nested object — rendered separately
                      "s360 questionnaire","s360 questionnaire submitted at",
                      // Internal routing / referral fields
                      "agent id","rep code","ref code","ref","repcode",
                      "referral id","referral ref id",
                      "referrer agent id","referring agent id",
                      "referrer agent contact id","affiliate ref id",
                      "lead vertical",
                    ]);
                    // Human-readable labels for new Share360 form fields stored in metadata
                    const SH360_KNOWN_LABELS: Record<string, string> = {
                      business_operation:                   "How does your business operate?",
                      business_processor:                   "Current processor (if you know it)",
                      business_card_volume:                 "Rough monthly card volume?",
                      business_supporting_nonprofit:        "Supporting Nonprofit Name",
                      business_interest:                    "I'd like to support this nonprofit through the program",
                      business_notes:                       "Anything you'd like us to know?",
                      nonprofit_website:                    "Website URL",
                      nonprofit_primary_contact_name:       "Primary Contact Name",
                      nonprofit_title_role:                 "Title/Role",
                      nonprofit_email:                      "Email Address",
                      nonprofit_phone:                      "Phone Number",
                      nonprofit_city_state:                 "City/State",
                      nonprofit_annual_impact:              "Approximately how many individuals do you impact annually?",
                      nonprofit_who_served:                 "Who They Serve",
                      nonprofit_programs_services:          "Programs & Services",
                      nonprofit_funding_sources:            "Funding Sources",
                      nonprofit_budget_goal:                "Budget Goal",
                      nonprofit_current_platforms:          "Current Platforms",
                      nonprofit_business_relationships:     "Business Relationships",
                      nonprofit_active_relationship_count:  "Active Business Relationships",
                      nonprofit_ambassador_available:       "Ambassador Available?",
                      nonprofit_reviewed_program:           "Reviewed Program?",
                      nonprofit_implementation_timeline:    "Implementation Timeline",
                      submitted_at:                         "Submitted At",
                    };
                    // Fields with long/multiline values — render full-width with pre-wrap
                    const SH360_LONG_FIELDS = new Set([
                      "nonprofit_programs_services",
                      "nonprofit_funding_sources",
                      "nonprofit_who_served",
                    ]);
                    const meta = lead.metadata as Record<string, unknown>;
                    const humanize = (k: string) => k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                    const label = (k: string) => SH360_KNOWN_LABELS[k] ?? humanize(k);
                    const extraKeys = Object.keys(meta).filter(k =>
                      meta[k] && typeof meta[k] === "string" && !SH360_SUPPRESS_NORM.has(normKey(k))
                    );
                    if (extraKeys.length === 0) return null;
                    return (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {extraKeys.map(k => (
                          <div key={k} className={`bg-white rounded-lg p-2.5${SH360_LONG_FIELDS.has(k) ? " col-span-2" : ""}`}>
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label(k)}</div>
                            <div className={`text-sm font-medium text-gray-800${SH360_LONG_FIELDS.has(k) ? " whitespace-pre-line" : ""}`}>{meta[k] as string}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Section C: Share360 Processing Questionnaire (submitted via client portal) */}
                  {(() => {
                    const q = (lead.metadata as any)?.s360_questionnaire as Record<string, string> | undefined;
                    if (!q || Object.keys(q).length === 0) return null;
                    const submittedAt = (lead.metadata as any)?.s360_questionnaire_submitted_at as string | undefined;
                    const Q_LABELS: Record<string, string> = {
                      business_legal_name:               "Legal Business / Org Name",
                      business_type:                     "Type of Business / Entity",
                      years_in_business:                 "Years in Business",
                      currently_processing:              "Currently Accepts Card Payments?",
                      current_processor:                 "Current Payment Processor",
                      monthly_card_volume:               "Est. Monthly Card Volume",
                      accepting_payments_methods:        "Payment Methods Used",
                      accepting_payments_terminal_type:  "Terminal Type",
                      accepting_payments_pos_name:       "POS System",
                      invoicing_over_50_percent:         "Invoicing >50%?",
                      payment_types_accepted:            "Payment Types Accepted",
                      payment_types_other_specify:       "Other Payment Type",
                      software_integrations:             "Software Integrations",
                      software_integrations_specify:     "Integration Details",
                      processing_goal:                   "Primary Goal for Share360",
                      referral_source:                   "How They Heard About Share360",
                      additional_notes:                  "Additional Notes",
                      nonprofit_name:                    "Nonprofit Name",
                      contact_person:                    "Contact Person",
                      email_address:                     "Email Address",
                      phone_number:                      "Phone Number",
                      nonprofit_address:                 "Nonprofit Address",
                      ein:                               "EIN #",
                      status_501c3:                      "501(c)(3) Status",
                      website:                           "Website",
                      facebook:                          "Facebook",
                      instagram:                         "Instagram",
                      other_social:                      "Other Social Media",
                      bank_name:                         "Bank Name",
                      routing_number:                    "Routing Number",
                      account_number:                    "Account Number",
                    };
                    const Q_CB_LABELS: Record<string, string> = {
                      credit_card_terminal:   "Credit card terminal",
                      pos_system:             "POS system",
                      online_customer_enters: "Online (customer-entered)",
                      online_merchant_enters: "Online (merchant-entered)",
                      phone_merchant_enters:  "Phone (merchant-entered)",
                      credit_debit_card:      "Credit/Debit card",
                      ach:                    "ACH",
                      gift_card:              "Gift Card",
                      other:                  "Other",
                      no_integrations:        "No",
                      yes_integrated:         "Yes",
                    };
                    const Q_LONG_KEYS = new Set(["additional_notes", "software_integrations_specify", "payment_types_other_specify"]);
                    const parseQVal = (k: string, v: string): string => {
                      try {
                        const arr = JSON.parse(v);
                        if (Array.isArray(arr)) return arr.map((x: string) => Q_CB_LABELS[x] ?? x.replace(/_/g, " ")).join(", ") || "None";
                      } catch { /* not JSON */ }
                      return v;
                    };
                    const isNpForm = !!(lead as any).verticalName?.toLowerCase().includes("nonprofit");
                    const sectionTitle = isNpForm ? "Nonprofit Questionnaire" : "Processing Questionnaire";
                    return (
                      <div className="mt-3">
                        <div className="text-[10px] font-bold text-[#1A6FD4]/70 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          {sectionTitle}
                          {submittedAt && (
                            <span className="font-normal normal-case text-gray-400 text-[10px]">
                              — {new Date(submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(q).filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className={`bg-white rounded-lg p-2.5 ${Q_LONG_KEYS.has(k) ? "col-span-2" : ""}`}>
                              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">
                                {Q_LABELS[k] ?? k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                              </div>
                              <div className="text-sm text-gray-800 font-medium">{parseQVal(k, v)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <RecordAgreementHistory kind="lead" id={lead.id} />

              {/* Client Documents - portal uploads + signed forms unified in one section */}
              {(() => {
                const portalDocs = clientDocsQuery.data ?? [];
                const completedSubs = (docusealSubmissionsQuery.data ?? []).filter((s: any) => s.status === "completed");
                // If email-matched submissions didn't find the Merchant Info Form, fall back to
                // the submission ID stored in lead metadata so it always appears in the lead card.
                const merchantSubmissionId = ((lead as any).metadata as any)?.s360_merchant_info?.docuseal_submission_id
                  ? parseInt(((lead as any).metadata as any).s360_merchant_info.docuseal_submission_id, 10)
                  : null;
                const emailMatchedMerchantSub = completedSubs.find((s: any) => s.templateId === 3538142);
                const metadataSub: any = (merchantSubmissionId && !isNaN(merchantSubmissionId) && !emailMatchedMerchantSub) ? {
                  id: merchantSubmissionId,
                  status: "completed",
                  templateName: "Share360 Merchant Information Form",
                  completedAt: ((lead as any).metadata as any)?.s360_merchant_info_submitted_at ?? null,
                  templateId: 3538142,
                } : null;
                const allSubs = metadataSub ? [...completedSubs, metadataSub] : completedSubs;
                const hasContent = portalDocs.length > 0 || allSubs.length > 0 || docusealSubmissionsQuery.isLoading;
                if (!hasContent) return null;
                const pendingCount = portalDocs.filter((d: any) => d.status === "pending" || d.status === "submitted").length;
                return (
                  <div className="border border-gray-200 rounded-xl p-4 bg-white">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Client Documents
                      {pendingCount > 0 && (
                        <span className="ml-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                          {pendingCount} pending
                        </span>
                      )}
                    </div>

                    {/* Portal-uploaded / submitted documents */}
                    {portalDocs.length > 0 && (
                      <div className="space-y-2">
                        {portalDocs.map((doc: any) => (
                          <div key={doc.id} className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-gray-800 truncate">{doc.docLabel ?? doc.docType}</div>
                                <div className="text-[10px] text-gray-400 truncate">{doc.fileName}</div>
                              </div>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${doc.status === "submitted" ? "bg-amber-100 text-amber-700" : doc.status === "approved" ? "bg-green-100 text-green-700" : doc.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                                {doc.status === "submitted" ? "Pending Review" : doc.status === "approved" ? "Approved" : doc.status === "rejected" ? "Rejected" : doc.status}
                              </span>
                              <a href={`/api/files/${doc.fileKey}`} target="_blank" rel="noopener noreferrer" title="View file" className="text-gray-400 hover:text-[#1A6FD4] flex-shrink-0">
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                              {isAdmin && doc.status !== "approved" && (
                                <button
                                  title="Approve"
                                  disabled={reviewDocMutation.isPending}
                                  onClick={() => reviewDocMutation.mutate({ docId: doc.id, status: "approved", reviewedBy: "Admin" })}
                                  className="p-1 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors flex-shrink-0"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {isAdmin && doc.status !== "rejected" && (
                                <button
                                  title="Reject"
                                  disabled={reviewDocMutation.isPending}
                                  onClick={() => { setRejectDocId(doc.id); setRejectNote(""); }}
                                  className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                                >
                                  <XCircle className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            {/* Inline rejection note for admin view */}
                            {doc.status === "rejected" && doc.reviewNote && (
                              <div className="ml-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
                                <p className="text-[10px] font-semibold text-red-600">Rejection reason:</p>
                                <p className="text-[10px] text-red-500">{doc.reviewNote}</p>
                              </div>
                            )}
                          </div>
                        ))}
                        {/* Rejection note modal */}
                        {rejectDocId !== null && (
                          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4">
                              <h3 className="text-sm font-bold text-gray-800 mb-1">Reject Document</h3>
                              <p className="text-xs text-gray-500 mb-3">Provide a reason so the client knows what to fix.</p>
                              <textarea
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                placeholder="e.g. SSN is missing. Driver's license number was not entered."
                                className="w-full text-xs border border-gray-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
                                rows={4}
                                autoFocus
                              />
                              <div className="flex gap-2 mt-3">
                                <button
                                  onClick={() => { setRejectDocId(null); setRejectNote(""); }}
                                  className="flex-1 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  disabled={!rejectNote.trim() || reviewDocMutation.isPending}
                                  onClick={() => reviewDocMutation.mutate({ docId: rejectDocId, status: "rejected", reviewNote: rejectNote.trim(), reviewedBy: "Admin" })}
                                  className="flex-1 py-2 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {reviewDocMutation.isPending ? "Rejecting…" : "Reject"}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Signed forms from DocuSeal */}
                    {(allSubs.length > 0 || docusealSubmissionsQuery.isLoading) && (
                      <div className={portalDocs.length > 0 ? "mt-2 pt-2 border-t border-gray-100 space-y-2" : "space-y-2"}>
                        {docusealSubmissionsQuery.isLoading ? (
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading signed forms…
                          </div>
                        ) : (
                          allSubs.map((s: any) => (
                            <DocusealSubmissionRow key={s.id} submission={s} />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Media360 Application Details */}
              {lead.verticalName === "Media360" && lead.metadata && Object.keys(lead.metadata).length > 0 && (() => {
                const m = lead.metadata as Record<string, string>;

                const KNOWN_LABELS: Record<string, string> = {
                  what_best_describes_your_current_stage:                             "Current Stage",
                  who_is_your_primary_audience_or_market:                             "Primary Audience",
                  how_soon_are_you_looking_to_get_started:                            "Timeline to Start",
                  have_you_invested_in_branding_marketing_or_media_exposure_before:   "Prior Brand Investment",
                  "on_a_scale_of_1_10_how_serious_are_you_about_becoming_a_recognized_authority": "Seriousness (1–10)",
                  are_you_open_to_collaboration_and_visibility_within_a_network:      "Open to Collaboration",
                  have_you_participated_in_a_collaborative_book_or_media_feature:     "Prior Collab / Media",
                  would_you_be_interested_in_sharing_your_story_in_media_or_a_book:   "Interested in Media/Book",
                  if_yes_who_would_represent_your_organization:                        "Organization Rep",
                  comfort_level_with_public_facing_content:                            "Public Content Comfort",
                  current_assets:                                                       "Current Assets",
                  opportunities_of_interest:                                            "Opportunities of Interest",
                  which_of_the_following_do_you_currently_have:                        "Current Assets",
                  which_opportunities_are_you_most_interested_in:                      "Opportunities of Interest",
                  submitted_at:                                                         "Submitted At",
                  primary_website_url:                                                  "Website",
                  instagram_profile_link:                                               "Instagram",
                  linkedin_profile_link:                                                "LinkedIn",
                  facebook_profile_or_business_page:                                   "Facebook",
                  additional_links:                                                     "Additional Links",
                  how_would_you_describe_what_you_currently_do:                        "What You Currently Do",
                  what_is_your_primary_goal_for_building_your_brand_right_now:         "Primary Brand Goal",
                  why_is_now_the_right_time_for_you_to_expand_your_visibility:         "Why Now?",
                  what_would_success_look_like_for_you_in_the_next_12_months:          "Success in 12 Months",
                  why_do_you_feel_media360_is_the_right_fit_for_you:                   "Why Media360?",
                  what_makes_your_story_expertise_or_brand_unique:                     "Unique Brand Story",
                  what_topics_would_you_be_interested_in_sharing:                      "Topics to Share",
                  biggest_value_of_participating_in_media_opportunities:               "Biggest Media Value",
                  anything_else_we_should_know:                                         "Anything Else",
                };
                const humanize = (key: string) =>
                  KNOWN_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

                // Normalise key → strip underscores, lowercase — catches every GHL key format.
                const normKey = (k: string) => k.replace(/_/g, " ").toLowerCase();
                const M360_SUPPRESS_NORM = new Set([
                  "lead type","role","vertical","source","submission type",
                  "route pipeline key","route pipeline name",
                  "utm source","utm medium","utm campaign",
                  "page title","page url","landing page","landing page url",
                  "form name","timestamp","lead vertical",
                  "first name","last name","full name","email","phone",
                  "agent id","ref code","rep code","ref","repcode",
                  "referral id","referral ref id",
                  "referrer agent id","referring agent id","referrer agent contact id",
                  "affiliate ref id","fpc ref id","fpc ref","contact type",
                ]);

                const LONG_KEYS = new Set([
                  "how_would_you_describe_what_you_currently_do",
                  "what_is_your_primary_goal_for_building_your_brand_right_now",
                  "why_is_now_the_right_time_for_you_to_expand_your_visibility",
                  "what_would_success_look_like_for_you_in_the_next_12_months",
                  "why_do_you_feel_media360_is_the_right_fit_for_you",
                  "what_makes_your_story_expertise_or_brand_unique",
                  "what_topics_would_you_be_interested_in_sharing",
                  "biggest_value_of_participating_in_media_opportunities",
                  "anything_else_we_should_know",
                ]);

                const answerKeys = Object.keys(m).filter(k => m[k] && !M360_SUPPRESS_NORM.has(normKey(k)));
                const shortKeys  = answerKeys.filter(k => !LONG_KEYS.has(k));
                const longKeys   = answerKeys.filter(k => LONG_KEYS.has(k));

                return (
                  <div className="border border-purple-200 rounded-xl p-4 bg-purple-50/50">
                    <div className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-3">
                      Media360 Application
                    </div>

                    {/* Associate Details */}
                    {(lead.repCode || lead.referrerAgentName || lead.referrerAgentEmail) && (
                      <div className="mb-3">
                        <div className="text-[10px] font-bold text-purple-700/70 uppercase tracking-wider mb-1.5">Associate Details</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ["Rep Code",       lead.repCode],
                            ["Referred By",    (lead as any).referrerAgentName],
                            ["Referrer Email", (lead as any).referrerAgentEmail],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label as string} className="bg-white rounded-lg p-2.5">
                              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                              <div className="text-sm font-medium text-gray-800 truncate">{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Submission Answers */}
                    {answerKeys.length > 0 && (
                      <>
                        <div className="text-[10px] font-bold text-purple-700/70 uppercase tracking-wider mb-1.5">Submission Answers</div>
                        {shortKeys.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                            {shortKeys.map(key => (
                              <div key={key} className="bg-white rounded-lg p-2.5">
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{humanize(key)}</div>
                                <div className="text-sm font-medium text-gray-800 truncate">{m[key]}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {longKeys.map(key => (
                          <div key={key} className="mt-2 bg-white rounded-lg p-2.5">
                            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{humanize(key)}</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{m[key]}</div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Notes */}
              {lead.notes && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <div className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> Lead Notes
                  </div>
                  <p className="text-sm text-amber-800 leading-relaxed">{lead.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Activity ── */}
          {activeTab === "activity" && (
            <div className="p-5">
              {activityQuery.isLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading activity...
                </div>
              ) : activityQuery.data && activityQuery.data.length > 0 ? (
                <div className="space-y-1">
                  {[...activityQuery.data].reverse().map((event) => (
                    <div key={event.id} className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ActivityIcon type={event.type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-700">{event.description}</div>
                        <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1.5">
                          <span>{event.actorName}</span>
                          <span>·</span>
                          <span>{timeAgo(event.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Activity className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">No activity recorded yet</p>
                  <p className="text-xs text-gray-300 mt-1">Activity will appear here as the lead progresses</p>
                </div>
              )}
            </div>
          )}

          {/* ── Notes ── */}
          {activeTab === "notes" && (
            <div className="p-5 flex flex-col gap-4">
              {/* Add note */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Add a Note</div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write a note about this lead..."
                  className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 focus:border-[#84ab4a] bg-white"
                  rows={3}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-[11px] text-gray-400">Logged as: <span className="font-semibold">{authorName}</span></span>
                  <button
                    onClick={handleAddNote}
                    disabled={!noteText.trim() || submittingNote}
                    className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg bg-[#84ab4a] text-white hover:bg-[#6d9040] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submittingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Save Note
                  </button>
                </div>
              </div>

              {/* Notes list */}
              {notesQuery.isLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading notes...
                </div>
              ) : notesQuery.data && notesQuery.data.length > 0 ? (
                <div className="space-y-3">
                  {/* Delete confirmation */}
                  {deleteNoteId !== null && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-xs mx-4">
                        <h3 className="text-sm font-bold text-gray-800 mb-2">Delete Note?</h3>
                        <p className="text-xs text-gray-500 mb-4">This will permanently remove the note.</p>
                        <div className="flex gap-2">
                          <button onClick={() => setDeleteNoteId(null)} className="flex-1 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                          <button
                            disabled={deleteNoteMutation.isPending}
                            onClick={() => deleteNoteMutation.mutate({ noteId: deleteNoteId })}
                            className="flex-1 py-2 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-50"
                          >
                            {deleteNoteMutation.isPending ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {[...notesQuery.data].reverse().map((note) => (
                    <div key={note.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#84ab4a]/20 flex items-center justify-center">
                            <User className="w-3 h-3 text-[#84ab4a]" />
                          </div>
                          <span className="text-xs font-semibold text-gray-700">{note.authorName}</span>
                          {note.repCode && (
                            <span className="text-[10px] font-mono text-gray-400">({note.repCode})</span>
                          )}
                          {(note as any).updatedAt && (
                            <span className="text-[10px] text-gray-400 italic">edited</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[11px] text-gray-400">{timeAgo(note.createdAt)}</span>
                          <button
                            title="Edit note"
                            onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.content); }}
                            className="p-1 rounded text-gray-300 hover:text-[#84ab4a] hover:bg-[#84ab4a]/10 transition-colors"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            title="Delete note"
                            onClick={() => setDeleteNoteId(note.id)}
                            className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {editingNoteId === note.id ? (
                        <div>
                          <textarea
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            className="w-full text-sm border border-gray-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 focus:border-[#84ab4a] bg-white"
                            rows={3}
                            autoFocus
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
                              className="flex-1 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              disabled={!editingNoteText.trim() || updateNoteMutation.isPending}
                              onClick={() => updateNoteMutation.mutate({ noteId: note.id, content: editingNoteText.trim() })}
                              className="flex-1 py-1.5 rounded-lg bg-[#84ab4a] text-white text-xs font-semibold hover:bg-[#6d9040] disabled:opacity-50"
                            >
                              {updateNoteMutation.isPending ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">No notes yet</p>
                  <p className="text-xs text-gray-300 mt-1">Add a note above to start tracking this lead</p>
                </div>
              )}

              {/* Tasks */}
              <TasksPanel leadId={lead.id} />
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
